import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import * as pako from "pako";
import {
  agGridOptionsNone,
  agGridTheme,
  customStyles,
  HOST_URL,
  MandatoryExplanation,
  MandatoryFieldIndicator,
  PERSONALHUB_PAGE,
} from "../../helpers/constants/Constants";
import Select from "react-select";
import Modal from "react-bootstrap/Modal";
import {
  ButtonGroup,
  Form,
  Button,
  Spinner,
} from "react-bootstrap";
import { AgGridReact } from "ag-grid-react";
import {
  ALL_PROJECT_USERS,
  ALL_USER_SUBMITTALS,
  APPROVER_ROLES,
  PH_REVIEWS_INFO,
  PH_REVIEWS_MARKUPS,
  REVIEWER_ROLES,
  SELECTED_PROJECT,
  updateGlobalState,
} from "../../helpers/reducers/GlobalReducer";
import {
  LAUNCH_VIEWER,
  MODEL_FILTER_STATE,
  MODEL_LOADED,
  SELECTED_MODELS,
  updateModelState,
} from "../../helpers/reducers/ModelReducer";
import {
  getAllProjectUsers,
  getMarkupById,
  updateSubmittalWithFile,
} from "../../helpers/axios/projectsAxios";
import Comments from "../comments/Comments";
import ScreenshotMarkup from "./ScreenshotMarkup";
import refresh from "../../assets/images/refresh.png";
import ReusableViewer from "../viewer/ReusableViewer";
import useMarkupRenderer from "./markupTools/useMarkupRenderer";

// Chunks are base64-encoded as text/plain (see below), so this is scaled
// down from 512KB to keep the encoded wire size per request unchanged.
const CHUNK_SIZE_BYTES = 384 * 1024;

// Encodes bytes as base64url (RFC 4648 §5) in chunks to avoid call-stack
// limits from String.fromCharCode.apply on large typed arrays. The '+'/'/'
// characters standard base64 uses are avoided since, combined with random
// 'for'/'if' substrings in large blobs, they can trip WAF Windows
// FOR/IF command-injection heuristics (e.g. matches on "for /", "if /").
const encodeBytesToBase64 = (bytes) => {
  let binary = "";
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    const slice = bytes.subarray(offset, offset + chunkSize);
    binary += String.fromCharCode.apply(null, slice);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

const getMarkupSortTimestamp = (markup) => {
  const candidates = [
    markup?.updatedAt,
    markup?.modifiedAt,
    markup?.createdAt,
    markup?.created,
    markup?.createdDate,
  ];

  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }

    const timestamp = Date.parse(candidate);
    if (Number.isFinite(timestamp)) {
      return timestamp;
    }
  }

  return 0;
};

const sortMarkupsNewestFirst = (markups) => {
  const list = Array.isArray(markups) ? [...markups] : [];

  return list.sort((a, b) => {
    const timeDelta = getMarkupSortTimestamp(b) - getMarkupSortTimestamp(a);
    if (timeDelta !== 0) {
      return timeDelta;
    }

    return Number(b?.id || 0) - Number(a?.id || 0);
  });
};

const PersHub = ({
  viewerState,
  onMarkupPreviewSelect,
  onMarkupPreviewLoadingChange,
}) => {
  const {
    phReviewsInfo,
    projectList,
    phReviewsMarkups,
    reviewerRoles,
    approverRoles,
    allProjectUsers,
    persHubNotifications,
    allUserSubmittals,
  } = useSelector((state) => state.globalReducer);
  const { selectedModels } = useSelector(
    (state) => state.modelReducer,
  );
  const userData = useSelector((state) => state.userData.data);
  const [selectedReview, setSelectedReview] = useState(null);
  const [selectedSubmittal, setSelectedSubmittal] = useState(null);
  const [selectedTab, setSelectedTab] = useState("submitted");
  const [createdByList, setCreatedByList] = useState("");
  const [markupFilterCat, setMarkupFilterCat] = useState();
  const [searchCriteria, setSearchCriteria] = useState("");
  const [filteredMarkups, setFilteredMarkups] = useState(
    sortMarkupsNewestFirst(phReviewsMarkups),
  );
  const [selectedMarkup, setSelectedMarkup] = useState();
  const [markupModal, setMarkupModal] = useState(false);
  const [submissionData, setSubmissionData] = useState();
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [submittalModal, setSubmittalModal] = useState(false);
  const [infoModal, setInfoModal] = useState(false);
  const [reviewSubmittal, setReviewSubmittal] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [markupView, setMarkupView] = useState(false);
  const [showMarkupModelsModal, setShowMarkupModelsModal] = useState(false);
  const [modalModels, setModalModels] = useState(null);
  const [viewerError, setViewerError] = useState(null);
  const [modalReady, setModalReady] = useState(false);
  const [selectedMarkupImage, setSelectedMarkupImage] = useState();
  const [selectedMarkupImageNotice, setSelectedMarkupImageNotice] =
    useState("");
  const [markupModalLoading, setMarkupModalLoading] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [pendingStatusMarkup, setPendingStatusMarkup] = useState(null);
  const [selectedStatusValue, setSelectedStatusValue] = useState("open");
  const [statusUpdateBusy, setStatusUpdateBusy] = useState(false);
  const [hasUnsavedModelManagementComment, setHasUnsavedModelManagementComment] =
    useState(false);
  const [unsavedCommentPromptOpen, setUnsavedCommentPromptOpen] =
    useState(false);
  const [pendingModelManagementAction, setPendingModelManagementAction] =
    useState(null);
  const [approveConfirmModalOpen, setApproveConfirmModalOpen] =
    useState(false);
  const [cancelRejectConfirmModalOpen, setCancelRejectConfirmModalOpen] =
    useState(false);
  const [issueRejectConfirmModalOpen, setIssueRejectConfirmModalOpen] =
    useState(false);
  const [issuingRejectionWithMarkup, setIssuingRejectionWithMarkup] =
    useState(false);
  const [reviewStatusModal, setReviewStatusModal] = useState({
    open: false,
    title: "",
    message: "",
  });
  const [submittalStatusModal, setSubmittalStatusModal] = useState({
    open: false,
    title: "",
    message: "",
  });
  const [activeMarkupCardId, setActiveMarkupCardId] = useState(null);
  const dispatch = useDispatch();

  const [markupImages, setMarkupImages] = useState({});
  const markupPreviewCacheRef = useRef(new Map());
  const markupPreviewListRef = useRef(null);
  const tabContentRef = useRef(null);
  const markupMainImageCacheRef = useRef(new Map());
  const rejectionMarkupAutoSaveRef = useRef(null);
  const markupEntityImageCacheRef = useRef(new Map());
  const onMarkupEntityImageLoaded = useCallback(() => {}, []);
  const {
    drawEntityPrimitive,
    getEntityBounds: getMarkupEntityBounds,
    getOrCreateImageEntityAsset,
  } = useMarkupRenderer(markupEntityImageCacheRef, onMarkupEntityImageLoaded);

  const registerRejectionMarkupAutoSave = useCallback((saveHandler) => {
    rejectionMarkupAutoSaveRef.current =
      typeof saveHandler === "function" ? saveHandler : null;
  }, []);

  // useEffect(() => {
  //   fetchUserReviews();
  //   fetchUserMarkups();
  //   fetchProjectList();
  // }, []);
  useEffect(() => {
    if (markupModal) {
      setViewerError(null);
      setModalReady(false);
      // Delay viewer initialization until modal is fully rendered
      setTimeout(() => {
        setModalReady(true);
      }, 1000); // Increased delay
    } else {
      setModalReady(false);
    }
  }, [markupModal]);

  useEffect(() => {
    if (phReviewsMarkups) {
      setFilteredMarkups(sortMarkupsNewestFirst(phReviewsMarkups));

      const tempCreatedBy = phReviewsMarkups.map((markup) => {
        return markup.userName;
      });
      if (tempCreatedBy) {
        const uniqueCreatedBy = removeDuplicates(tempCreatedBy);
        setCreatedByList(uniqueCreatedBy);
      }
    }
  }, [phReviewsMarkups]);

  const updateProjectUsers = useCallback(async (review) => {
    const allProjectUsers = await getAllProjectUsers(
      review.projectAssetTask.projectAsset.project.accId,
    );
    const sortedData = allProjectUsers.sort((a, b) =>
      a.name.localeCompare(b.name),
    );
    dispatch(
      updateGlobalState({
        value: sortedData,
        stateName: ALL_PROJECT_USERS,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    if (selectedTab !== "markup" || !markupPreviewListRef.current) {
      return undefined;
    }

    const controller = new AbortController();
    const queuedIds = new Set();
    const queue = [];
    let activeRequests = 0;

    const loadPreview = async (markupId) => {
      activeRequests += 1;
      let imagePreview = null;
      try {
        const response = await axios.get(
          `${HOST_URL}/markups/getMarkupPreview/${markupId}`,
          { responseType: "text", withCredentials: true, signal: controller.signal },
        );
        imagePreview = response.data || null;
        if (
          typeof imagePreview === "string" &&
          imagePreview.startsWith("data:") &&
          !imagePreview.startsWith("data:image/")
        ) {
          try {
            const decoded = atob(imagePreview.split(",")[1]);
            if (decoded.startsWith("data:image/")) {
              imagePreview = decoded;
            }
          } catch {}
        }
      } catch {}

      if (!controller.signal.aborted) {
        const preview = { imagePreview, imageError: !imagePreview };
        markupPreviewCacheRef.current.set(markupId, preview);
        setMarkupImages((prev) => ({ ...prev, [markupId]: preview }));
      }
      activeRequests -= 1;
      drainQueue();
    };

    const drainQueue = () => {
      while (!controller.signal.aborted && activeRequests < 4 && queue.length) {
        loadPreview(queue.shift());
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }
          const markupId = entry.target.dataset.markupId;
          observer.unobserve(entry.target);
          if (!markupPreviewCacheRef.current.has(markupId) && !queuedIds.has(markupId)) {
            queuedIds.add(markupId);
            queue.push(markupId);
          }
        });
        drainQueue();
      },
      { root: tabContentRef.current, rootMargin: "400px 0px", threshold: 0.01 },
    );

    markupPreviewListRef.current.querySelectorAll("[data-markup-id]").forEach((node) => {
      if (!markupPreviewCacheRef.current.has(node.dataset.markupId)) {
        observer.observe(node);
      }
    });

    return () => {
      observer.disconnect();
      controller.abort();
    };
  }, [filteredMarkups, selectedTab]);

  const normalizeImageDataUrl = (rawValue) => {
    if (!rawValue) {
      return null;
    }

    const rawString = String(rawValue).trim();
    if (!rawString) {
      return null;
    }

    if (rawString.startsWith("data:image/")) {
      return rawString;
    }

    if (rawString.startsWith("data:")) {
      try {
        const encoded = rawString.split(",")[1] || "";
        const decoded = atob(encoded);
        if (decoded.startsWith("data:image/")) {
          return decoded;
        }
      } catch (err) {
        return null;
      }
    }

    if (/^[A-Za-z0-9+/=\s]+$/.test(rawString) && rawString.length > 96) {
      const compact = rawString.replace(/\s+/g, "");
      return `data:image/png;base64,${compact}`;
    }

    return null;
  };

  const tryDecodeBufferLike = (candidate) => {
    if (!candidate) {
      return null;
    }

    if (
      candidate &&
      typeof candidate === "object" &&
      candidate.type === "Buffer" &&
      Array.isArray(candidate.data)
    ) {
      try {
        return new TextDecoder().decode(Uint8Array.from(candidate.data));
      } catch (err) {
        return null;
      }
    }

    if (candidate instanceof Uint8Array) {
      try {
        return new TextDecoder().decode(candidate);
      } catch (err) {
        return null;
      }
    }

    if (Array.isArray(candidate)) {
      try {
        return new TextDecoder().decode(Uint8Array.from(candidate));
      } catch (err) {
        return null;
      }
    }

    if (candidate instanceof ArrayBuffer) {
      try {
        return new TextDecoder().decode(new Uint8Array(candidate));
      } catch (err) {
        return null;
      }
    }

    if (ArrayBuffer.isView(candidate)) {
      try {
        return new TextDecoder().decode(new Uint8Array(candidate.buffer));
      } catch (err) {
        return null;
      }
    }

    if (
      typeof candidate === "object" &&
      !Array.isArray(candidate) &&
      Object.keys(candidate).length > 0 &&
      Object.keys(candidate).every((key) => /^\d+$/.test(key))
    ) {
      try {
        const bytes = Object.keys(candidate)
          .sort((a, b) => Number(a) - Number(b))
          .map((key) => Number(candidate[key]))
          .filter((value) => Number.isFinite(value));
        return new TextDecoder().decode(Uint8Array.from(bytes));
      } catch (err) {
        return null;
      }
    }

    return null;
  };

  const normalizeMarkupByIdPayload = (response) => {
    const decodePayloadCandidate = (candidate) => {
      if (!candidate) {
        return null;
      }

      if (typeof candidate === "string") {
        try {
          return JSON.parse(candidate);
        } catch (err) {
          return null;
        }
      }

      const decoded = tryDecodeBufferLike(candidate);
      if (decoded) {
        try {
          return JSON.parse(decoded);
        } catch (err) {
          return null;
        }
      }

      if (typeof candidate === "object" && !Array.isArray(candidate)) {
        return candidate;
      }

      return null;
    };

    const looksLikeMarkupRecord = (candidate) => {
      if (
        !candidate ||
        typeof candidate !== "object" ||
        Array.isArray(candidate)
      ) {
        return false;
      }

      const recordKeys = [
        "id",
        "title",
        "markupTitle",
        "imagePreview",
        "markupState",
        "category",
        "userName",
        "created",
        "createdFor",
        "pin",
        "data",
        "markupData",
      ];

      return recordKeys.some((key) =>
        Object.prototype.hasOwnProperty.call(candidate, key),
      );
    };

    const data = decodePayloadCandidate(response?.data);
    if (!data || typeof data !== "object" || Array.isArray(data)) {
      return null;
    }

    if (looksLikeMarkupRecord(data)) {
      return data;
    }

    const nestedParsed = decodePayloadCandidate(data.data);
    if (
      nestedParsed &&
      typeof nestedParsed === "object" &&
      !Array.isArray(nestedParsed)
    ) {
      if (looksLikeMarkupRecord(nestedParsed)) {
        return nestedParsed;
      }

      const wrapperKeys = ["data", "status", "success", "message", "error"];
      const isWrapper =
        Object.keys(data).length <= 6 &&
        Object.keys(data).every((key) => wrapperKeys.includes(key));

      if (isWrapper) {
        return nestedParsed;
      }
    }

    return data;
  };

  const parsePersistedMarkupData = (source) => {
    let current = source;

    for (let i = 0; i < 4; i++) {
      if (!current) {
        return null;
      }

      if (typeof current === "string") {
        const trimmed = current.trim();
        if (!trimmed) {
          return null;
        }
        try {
          current = JSON.parse(trimmed);
          continue;
        } catch (err) {
          return null;
        }
      }

      const decoded = tryDecodeBufferLike(current);
      if (decoded) {
        current = decoded;
        continue;
      }

      if (typeof current === "object" && !Array.isArray(current)) {
        return current;
      }

      return null;
    }

    return null;
  };

  const composeMarkupScreenshot = async (baseScreenshot, overlayScreenshot, persistedData) => {
    if (!baseScreenshot) {
      return overlayScreenshot || null;
    }

    const loadImage = (src) =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("Image decode failed"));
        img.src = src;
      });

    try {
      const [baseImage, overlayImage] = await Promise.all([
        loadImage(baseScreenshot),
        overlayScreenshot ? loadImage(overlayScreenshot).catch(() => null) : null,
      ]);

      const width = baseImage.naturalWidth || baseImage.width;
      const height = baseImage.naturalHeight || baseImage.height;
      if (!width || !height) {
        return baseScreenshot;
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        return baseScreenshot;
      }

      ctx.drawImage(baseImage, 0, 0, width, height);

      const hasOverlay = Boolean(overlayImage);
      if (hasOverlay) {
        ctx.drawImage(overlayImage, 0, 0, width, height);
      }

      const entities = Array.isArray(persistedData?.entities)
        ? persistedData.entities
        : [];

      await Promise.all(
        entities
          .filter((entity) => entity?.type === "image")
          .map((entity) =>
            getOrCreateImageEntityAsset(entity.imageDataUrl || entity.imageSrc)?.ready,
          ),
      );

      if (entities.length) {
        entities.forEach((entity) => {
          if (!entity || typeof entity !== "object") {
            return;
          }
          const rotation = Number(entity?.rotation) || 0;
          if (!rotation) {
            drawEntityPrimitive(ctx, entity);
            return;
          }

          const bounds = getMarkupEntityBounds(entity, ctx);
          const cx = (bounds.left + bounds.right) / 2;
          const cy = (bounds.top + bounds.bottom) / 2;

          ctx.save();
          ctx.translate(cx, cy);
          ctx.rotate(rotation);
          ctx.translate(-cx, -cy);
          drawEntityPrimitive(ctx, entity);
          ctx.restore();
        });
      }

      return canvas.toDataURL("image/png");
    } catch (err) {
      return baseScreenshot;
    }
  };

  const fetchMainMarkupScreenshot = async (markup) => {
    const markupId = Number(markup?.id);
    if (!Number.isInteger(markupId) || markupId <= 0) {
      setSelectedMarkupImage(null);
      setSelectedMarkupImageNotice(
        "No markup image is available because this rejection was submitted without a saved markup.",
      );
      return null;
    }

    const cached = markupMainImageCacheRef.current.get(markupId);
    if (cached) {
      setSelectedMarkupImageNotice("");
      setSelectedMarkupImage(cached);
      return cached;
    }

    try {
      const response = await getMarkupById(markupId);
      const payload = normalizeMarkupByIdPayload(response) || markup;

      const parsePersistedCandidate = (candidate) => {
        if (!candidate) {
          return null;
        }

        const parsed = parsePersistedMarkupData(candidate);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
          return null;
        }

        const hasMarkupPayload =
          typeof parsed?.screenshotDataUrl === "string" ||
          typeof parsed?.imageUrl === "string" ||
          typeof parsed?.screenshotUrl === "string" ||
          typeof parsed?.drawOverlayDataUrl === "string" ||
          (Array.isArray(parsed?.entities) && parsed.entities.length > 0) ||
          parsed?.source === "ScreenshotMarkup";

        if (hasMarkupPayload) {
          return parsed;
        }

        const nested =
          parsePersistedMarkupData(parsed?.markupData) ||
          parsePersistedMarkupData(parsed?.data) ||
          null;

        if (
          nested &&
          typeof nested === "object" &&
          !Array.isArray(nested) &&
          (typeof nested?.screenshotDataUrl === "string" ||
            typeof nested?.imageUrl === "string" ||
            typeof nested?.screenshotUrl === "string" ||
            typeof nested?.drawOverlayDataUrl === "string" ||
            (Array.isArray(nested?.entities) && nested.entities.length > 0) ||
            nested?.source === "ScreenshotMarkup")
        ) {
          return nested;
        }

        return null;
      };

      const persisted =
        parsePersistedCandidate(payload?.data) ||
        parsePersistedCandidate(payload?.markupData) ||
        parsePersistedCandidate(payload?.data?.markupData) ||
        parsePersistedCandidate(payload?.markup?.data) ||
        parsePersistedCandidate(payload?.markup?.markupData) ||
        parsePersistedCandidate(payload) ||
        null;

      const baseScreenshot = normalizeImageDataUrl(
        persisted?.screenshotDataUrl ||
        persisted?.screenshotUrl ||
          persisted?.imageUrl ||
          payload?.screenshotDataUrl ||
          payload?.screenshotUrl ||
          payload?.imageUrl ||
          payload?.data?.screenshotDataUrl ||
          payload?.data?.screenshotUrl ||
          payload?.markup?.screenshotDataUrl ||
          payload?.markup?.screenshotUrl,
      );
      const overlayScreenshot = normalizeImageDataUrl(
        persisted?.drawOverlayDataUrl ||
          payload?.drawOverlayDataUrl ||
          payload?.data?.drawOverlayDataUrl ||
          payload?.markup?.drawOverlayDataUrl,
      );

      const shouldCompose = Boolean(baseScreenshot || overlayScreenshot);

      const composedImage = shouldCompose
        ? await composeMarkupScreenshot(
            baseScreenshot,
            overlayScreenshot,
            persisted,
          )
        : null;

      if (composedImage) {
        markupMainImageCacheRef.current.set(markupId, composedImage);
        setSelectedMarkupImageNotice("");
        setSelectedMarkupImage(composedImage);
        return composedImage;
      }

      setSelectedMarkupImageNotice(
        "Unable to render markup image from the saved screenshot and annotations.",
      );
      setSelectedMarkupImage(null);
      return null;
    } catch (err) {
      console.error(`Failed to load full screenshot for markup ${markupId}:`, err);
    }

    setSelectedMarkupImageNotice(
      "No markup image is available because this rejection could not be reconstructed from saved screenshot data.",
    );
    setSelectedMarkupImage(null);
    return null;
  };

  const onMarkupCardClick = async (markup) => {
    const project = projectList?.filter((project) => {
      return project.id.includes(markup.projectId);
    })[0];

    const markupWithProjectType = {
      ...markup,
      projectType: project?.mviewProjectType || "Unknown",
    };

    setActiveMarkupCardId(markup.id);
    setSelectedMarkup(markupWithProjectType);

    if (typeof onMarkupPreviewLoadingChange === "function") {
      onMarkupPreviewLoadingChange(true);
    }

    let imagePreview = null;
    try {
      imagePreview = await fetchMainMarkupScreenshot(markup);
    } finally {
      if (typeof onMarkupPreviewLoadingChange === "function") {
        onMarkupPreviewLoadingChange(false);
      }
    }

    if (typeof onMarkupPreviewSelect === "function") {
      onMarkupPreviewSelect({
        id: markup.id,
        title: markup.title,
        image: imagePreview,
      });
    }
  };

  const updateRoles = useCallback((discipline) => {
    switch (discipline) {
      case "Civil":
      case "Civil Engineer":
        dispatch(
          updateGlobalState({
            value: [
              "Civil Reviewer",
              "Civil Approver",
              "Design Manager",
              "Admin",
            ],
            stateName: REVIEWER_ROLES,
          }),
        );
        dispatch(
          updateGlobalState({
            value: ["Civil Approver", "Design Manager", "Admin"],
            stateName: APPROVER_ROLES,
          }),
        );
        break;
      case "Mechanical":
        dispatch(
          updateGlobalState({
            value: [
              "Mechanical Reviewer",
              "Mechanical Approver",
              "Design Manager",
              "Admin",
            ],
            stateName: REVIEWER_ROLES,
          }),
        );

        dispatch(
          updateGlobalState({
            value: ["Mechanical Approver", "Design Manager", "Admin"],
            stateName: APPROVER_ROLES,
          }),
        );
        break;
      case "Electrical":
      case "EICA":
        dispatch(
          updateGlobalState({
            value: [
              "Electrical Reviewer", // need this one or just CAD and DM?
              "Electrical Approver", // need this one or just CAD and DM?
              "Design Manager",
              "Admin",
            ],
            stateName: REVIEWER_ROLES,
          }),
        );
        dispatch(
          updateGlobalState({
            value: [
              "Electrical Approver", // need this one or just CAD and DM?
              "Design Manager",
              "Admin",
            ],
            stateName: APPROVER_ROLES,
          }),
        );
        break;
      case "Structural":
        dispatch(
          updateGlobalState({
            value: [
              "Structural Reviewer",
              "Structural Approver",
              "Design Manager",
              "Admin",
            ],
            stateName: REVIEWER_ROLES,
          }),
        );
        dispatch(
          updateGlobalState({
            value: ["Structural Approver", "Design Manager", "Admin"],
            stateName: APPROVER_ROLES,
          }),
        );
        break;
      case "General":
      case "Multi-disciplinary":
        dispatch(
          updateGlobalState({
            value: [
              "Civil Reviewer",
              "Civil Approver",
              "Mechanical Reviewer",
              "Mechanical Approver",
              "Electrical Reviewer",
              "Electrical Approver",
              "Structural Reviewer",
              "Structural Approver",
              "Process Reviewer",
              "Process Approver",
              "Design Manager",
              "Admin",
            ],
            stateName: REVIEWER_ROLES,
          }),
        );
        dispatch(
          updateGlobalState({
            value: [
              "Civil Approver",
              "Mechanical Approver",
              "Electrical Approver",
              "Structural Approver",
              "Process Approver",
              "Design Manager",
              "Admin",
            ],
            stateName: APPROVER_ROLES,
          }),
        );
        break;

      default:
        dispatch(
          updateGlobalState({
            value: ["Design Manager"],
            stateName: REVIEWER_ROLES,
          }),
        );
        dispatch(
          updateGlobalState({
            value: ["Design Manager"],
            stateName: APPROVER_ROLES,
          }),
        );
        break;
    }
  }, [dispatch]);

  useEffect(() => {
    if (selectedReview) {
      updateRoles(selectedReview.discipline);
      updateProjectUsers(selectedReview);
    }
  }, [selectedReview, updateProjectUsers, updateRoles]);

  const removeDuplicates = (arr) => {
    return arr.filter((item, index) => arr.indexOf(item) === index);
  };

  const fetchUserReviews = () => {
    axios({
      method: "get",
      url: HOST_URL + "/phReviews/getUserReviews",
      params: { email: userData.emailId.toLowerCase() },
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      withCredentials: true,
    })
      .then((response) => {
        dispatch(
          updateGlobalState({
            value: response.data,
            stateName: PH_REVIEWS_INFO,
          }),
        );
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const fetchUserMarkups = useCallback(() => {
    axios({
      method: "get",
      url: HOST_URL + "/phReviews/getUserMarkups",
      params: { email: userData.emailId.toLowerCase() },
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      withCredentials: true,
    })
      .then((response) => {
        dispatch(
          updateGlobalState({
            value: response.data,
            stateName: PH_REVIEWS_MARKUPS,
          }),
        );
      })
      .catch((error) => {
        console.log(error);
      });
  }, [dispatch, userData.emailId]);

  const loadSubmittalModel = (submittal) => {
    dispatch(
      updateModelState({
        value: false,
        stateName: LAUNCH_VIEWER,
      }),
    );
    //axios call to fetch model details
    axios({
      method: "get",
      url: HOST_URL + "/forgeData/getSubmittalModel",
      params: {
        docNumber: submittal.projectAssetTask.docNumber,
        projectId: submittal.projectAssetTask.projectAsset.project.accId,
      },
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      withCredentials: true,
    })
      .then((response) => {
        //set state as selected Model with data back from axios call
        //take project info from submittal
        const tempProject = {
          id: submittal.projectAssetTask.projectAsset.project.accId,
          text: submittal.projectAssetTask.projectAsset.project.name,
          type: "bim360projects",
          children: true,
          mviewProjectInfo: submittal.projectAssetTask.projectAsset.project,
          projectCode:
            submittal.projectAssetTask.projectAsset.project.projectCode,
        };
        const tempSelectedModels = [
          {
            id: response.data.relationships.derivatives.data.id,
            documentName: response.data.attributes.name,
            storageLocation: response.data.relationships.storage.meta.link.href,
            deleteId: response.data.relationships.item.data.id,
            // task: mviewData.task,
            // status: mviewData.status,
            // revision: mviewData.revision,
            // projectAssetTaskId: mviewData.id,
          }, //RM TODO ADD DATA HERE SO AS MARKUPS WORK IN VIEWER
        ];
        dispatch(
          updateModelState({
            value: tempSelectedModels,
            stateName: SELECTED_MODELS,
          }),
        );
        dispatch(
          updateGlobalState({
            value: [tempProject],
            stateName: SELECTED_PROJECT,
          }),
        );
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {});
  };

  const loadModelHandler = (id) => {
    dispatch(
      updateModelState({
        value: false,
        stateName: LAUNCH_VIEWER,
      }),
    );
    axios({
      method: "get",
      url: HOST_URL + "/phReviews/getReview",
      params: { id: id },
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      withCredentials: true,
    })
      .then((response) => {
        const tempProject = {
          id: response.data.review.projectAssetTask.projectAsset.project.accId,
          text: response.data.review.projectAssetTask.projectAsset.project.name,
          type: "bim360projects",
          children: true,
          mviewProjectInfo:
            response.data.review.projectAssetTask.projectAsset.project,
          projectCode:
            response.data.review.projectAssetTask.projectAsset.project
              .projectCode,
        };
        dispatch(
          updateGlobalState({
            value: [tempProject],
            stateName: SELECTED_PROJECT,
          }),
        );
        dispatch(
          updateModelState({
            value: response.data.review.selectedModels,
            stateName: SELECTED_MODELS,
          }),
        );
        setSelectedReview(response.data.review);

        dispatch(
          updateModelState({
            value: {
              process: {
                processArea: response.data.review.selectedModels[0].asset,
              },
            },
            stateName: MODEL_FILTER_STATE,
          }),
        );
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {});
  };

  const launchModelViewer = () => {
    if (selectedModels.length > 0) {
      dispatch(
        updateModelState({
          value: true,
          stateName: LAUNCH_VIEWER,
        }),
      );
      dispatch(
        updateModelState({
          value: false,
          stateName: MODEL_LOADED,
        }),
      );
    } else {
      alert("Please select a model to view.");
    }
  };

  const updateReview = (status, markupId = null) => {
    var review = selectedReview;
    // var category = selectedModelCategory(selectedReview);
    review.markupId = markupId;

    if (status === "Reject") {
      switch (selectedTab) {
        case "check":
          review.checkedDate = "rejected";
          review.reviewedDate = "rejected";
          review.approvedDate = "rejected";
          break;
        case "review":
          review.reviewedDate = "rejected";
          review.approvedDate = "rejected";
          break;
        case "approval":
          review.approvedDate = "rejected";
          break;
        default:
          break;
      }
    } else {
      const today = new Date();
      const yyyy = today.getFullYear();
      let mm = today.getMonth() + 1; // Months start at 0!
      let dd = today.getDate();

      if (dd < 10) dd = "0" + dd;
      if (mm < 10) mm = "0" + mm;

      const formattedToday = dd + "/" + mm + "/" + yyyy;

      switch (selectedTab) {
        case "check":
          review.checkedDate = formattedToday;
          review.reviewedBy = submissionData.reviewer;
          review.approvedBy = submissionData.approver;
          onCloseModal();

          break;
        case "review":
          review.reviewedDate = formattedToday;
          review.approvedBy = submissionData.approver;
          onCloseModal();
          break;
        case "approval":
          review.approvedDate = formattedToday;
          break;
        case "submitted":
          if (review.checkedDate === "rejected") {
            review.checkedDate = "archived";
          }
          if (review.reviewedDate === "rejected") {
            review.reviewedDate = "archived";
          }
          if (review.approvedDate === "rejected") {
            review.approvedDate = "archived";
          }
          break;
        default:
          break;
      }
    }

    setSelectedReview(null);

    axios({
      method: "post",
      url: HOST_URL + "/phReviews/updateReview",
      data: { review },
      // headers: {
      //   "Content-Type": "application/x-www-form-urlencoded",
      // },
      withCredentials: true,
    })
      .then((response) => {
        fetchUserReviews();
        switch (status) {
          case "Reject":
            setReviewStatusModal({
              open: true,
              title: "Review Rejected",
              message: "Review rejected successfully.",
            });
            break;
          case "Archive":
            setReviewStatusModal({
              open: true,
              title: "Review Archived",
              message: "Review archived successfully.",
            });
            break;

          case "check":
          case "review":
          case "Approve":
            setReviewStatusModal({
              open: true,
              title: "Review Approved",
              message: "Review approved successfully.",
            });

            break;

          default:
            break;
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const deleteReview = (id) => {
    if (window.confirm("Are you sure you want to delete this review?")) {
      axios({
        method: "delete",
        url: HOST_URL + "/phReviews/deleteReview",
        params: { id: id },
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      })
        .then((response) => {
          fetchUserReviews();
          setSelectedReview(null);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  // const completeReview = (id) => {
  //   axios({
  //     method: "post",
  //     url: HOST_URL + "/phReviews/completeReview",
  //     params: { id: id },
  //     headers: {
  //       "Content-Type": "application/x-www-form-urlencoded",
  //     },
  //     withCredentials: true,
  //   })
  //     .then((response) => {
  //       fetchUserReviews();
  //       setSelectedReview(null);
  //     })
  //     .catch((error) => {
  //       console.log(error);
  //     });
  // };

  // const selectedModelCategory = (selectedReviewData) => {
  //   switch (selectedReviewData.status) {
  //     case "for review":
  //       if (selectedReviewData.reviewer.toLowerCase() === userData.emailId.toLowerCase()) {
  //         return "Review";
  //       } else {
  //         return "Submission";
  //       }

  //     case "for approval":
  //       if (selectedReviewData.approver.toLowerCase() === userData.emailId.toLowerCase()) {
  //         return "Approval";
  //       } else {
  //         return "Submission";
  //       }
  //   }
  // }

  //     case "approved":
  //       return "Archived";

  //     case "rejected":
  //       return "Archived";

  //     default:
  //       break;
  //   }
  // };

  // const submitReview = () => {
  //   //add upload to db here
  //   const review = {
  //     projectName: "J969.01 - Shortlands WBS Pump Starters & Pipework",
  //     documentName: "Mass Balance",
  //     docStatus: "S01.1",
  //     status: "for review",
  //     submittedBy: "richard.morris@mwhtreatment.com",
  //     reviewer: "rob.field@mwhtreatment.com",
  //     approver: "mark.hollingsworth@mwhtreatment.com",
  //     comments: "Please review",
  //     file: "insert file here",
  //   };
  //   let formData = new FormData();
  //   formData.append("formData", JSON.stringify(review));

  //   axios({
  //     method: "post",
  //     url: HOST_URL + "/phReviews/saveReview",
  //     data: formData,
  //     headers: {
  //       "Content-Type": "application/x-www-form-urlencoded",
  //     },
  //     withCredentials: true,
  //   })
  //     .then((response) => {
  //     })
  //     .catch((error) => {
  //       console.log(error);
  //     });
  // };

  // const checkboxChangeHandler = (e) => {
  //   let tempMarkups2 = {
  //     data: phReviewsMarkups.filter(
  //       (markup) => markup.id.toString() === e.target.id //change to .value
  //     ),
  //   };
  //   tempMarkups2.data[0][e.target.name] = e.target.checked;

  //   const tempMarkups = phReviewsMarkups.map((markup) => {
  //     if (markup.id.toString() === e.target.id) {
  //       return {
  //         ...markup,
  //         [e.target.name]: e.target.checked,
  //       };
  //     } else {
  //       return { ...markup };
  //     }
  //   });

  //   dispatch(
  //     updateGlobalState({
  //       value: tempMarkups,
  //       stateName: PH_REVIEWS_MARKUPS,
  //     })
  //   );
  //   updateMarkup(tempMarkups2.data[0]);
  // };
  // const toggleButtonChangeHandler = (e) => {
  //   let tempMarkups2 = {
  //     data: phReviewsMarkups.filter(
  //       (markup) => markup.id.toString() === e.target.value
  //     ),
  //   };
  //   tempMarkups2.data[0][e.target.id] = !tempMarkups2.data[0][e.target.id];

  //   const tempMarkups = phReviewsMarkups.map((markup) => {
  //     if (markup.id.toString() === e.target.id) {
  //       return {
  //         ...markup,
  //         [e.target.name]: e.target.checked,
  //       };
  //     } else {
  //       return { ...markup };
  //     }
  //   });
  //   dispatch(
  //     updateGlobalState({
  //       value: tempMarkups,
  //       stateName: PH_REVIEWS_MARKUPS,
  //     })
  //   );
  //   updateMarkup(tempMarkups2.data[0]);
  // };
  const postChunkedCompressedMarkupUpdate = useCallback(
    async (payload, uploadScope = "pershubUpdateMarkup") => {
      const compressedPayload = pako.deflate(JSON.stringify(payload));
      const payloadLength = Number(compressedPayload?.length || 0);

      if (!payloadLength) {
        throw new Error("No payload provided for upload");
      }

      const totalChunks = Math.max(
        1,
        Math.ceil(payloadLength / CHUNK_SIZE_BYTES),
      );
      const uploadId = `${uploadScope}-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 10)}`;

      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex += 1) {
        const start = chunkIndex * CHUNK_SIZE_BYTES;
        const end = Math.min(start + CHUNK_SIZE_BYTES, payloadLength);
        const chunk = compressedPayload.subarray(start, end);
        // JSON + base64 avoids WAF rules blocking application/octet-stream and text/plain.
        const chunkBase64 = encodeBytesToBase64(chunk);
        const isFinalChunk = chunkIndex === totalChunks - 1;

        await axios({
          method: "post",
          url: HOST_URL + "/markups/updateMarkup",
          data: { data: chunkBase64 },
          headers: {
            "Content-Type": "application/json",
            "X-Upload-Id": uploadId,
            "X-Chunk-Index": String(chunkIndex),
            "X-Chunk-Count": String(totalChunks),
            "X-Chunk-Size": String(chunk.length),
            "X-Original-Size": String(payloadLength),
            "X-Is-Final-Chunk": String(isFinalChunk),
            "X-Compression": "deflate",
            "X-Chunk-Encoding": "base64url",
          },
          withCredentials: true,
        });
      }
    },
    [],
  );

  const updateMarkup = useCallback(
    async (markup) => {
      try {
        await postChunkedCompressedMarkupUpdate(markup, "pershubUpdateMarkup");
        fetchUserMarkups();
        // fetchMarkups(modelFilterState.process);
      } catch (error) {
        console.log(error);
      }
    },
    [fetchUserMarkups, postChunkedCompressedMarkupUpdate],
  );

  const markupFilterHandler = (e) => {
    setMarkupFilterCat(e.value);
    setSearchCriteria("");
    setFilteredMarkups(sortMarkupsNewestFirst(phReviewsMarkups));
  };

  const selectOptions = [
    { value: "", label: "No Filter" },
    { value: "project", label: "Project" },
    { value: "actioned", label: "Completed" },
    { value: "User", label: "Created by" },
    { value: "approved", label: "Approved" },
  ];
  const actionOptions = [
    { value: "true", label: "Yes" },
    { value: "false", label: "No" },
  ];
  const markupStatusOptions = [
    { value: "open", label: "Open" },
    { value: "under-review", label: "Under review" },
    { value: "closed", label: "Closed" },
  ];

  const getMarkupStatusDisplay = useCallback((markup) => {
    const normalizedStatus = String(markup?.status || "")
      .trim()
      .toLowerCase();

    if (normalizedStatus === "closed" || Boolean(markup?.actioned)) {
      return {
        value: "closed",
        label: "Closed",
        badgeStyle: {
          color: "#ffffff",
          background: "linear-gradient(135deg, #43a047, #1b5e20)",
          border: "1px solid rgba(255, 255, 255, 0.22)",
        },
      };
    }

    if (
      normalizedStatus === "under review" ||
      normalizedStatus === "under_review" ||
      normalizedStatus === "under-review" ||
      Boolean(markup?.approved)
    ) {
      return {
        value: "under-review",
        label: "Under review",
        badgeStyle: {
          color: "#ffffff",
          background: "linear-gradient(135deg, #ffb300, #f9a825)",
          border: "1px solid rgba(255, 255, 255, 0.22)",
        },
      };
    }

    return {
      value: "open",
      label: "Open",
      badgeStyle: {
        color: "#ffffff",
        background: "linear-gradient(135deg, #e53935, #b71c1c)",
        border: "1px solid rgba(255, 255, 255, 0.22)",
      },
    };
  }, []);

  const buildMarkupStatusPayload = useCallback((markup, statusValue) => {
    const normalizedStatus = String(statusValue || "open").trim().toLowerCase();

    if (normalizedStatus === "closed") {
      return {
        status: "Closed",
        approved: true,
        actioned: true,
      };
    }

    if (normalizedStatus === "under-review") {
      return {
        status: "Under review",
        approved: true,
        actioned: false,
      };
    }

    return {
      status: "Open",
      approved: false,
      actioned: false,
    };
  }, []);

  const openStatusModal = useCallback(
    (markup) => {
      if (!markup) {
        return;
      }
      setPendingStatusMarkup(markup);
      setSelectedStatusValue(getMarkupStatusDisplay(markup).value || "open");
      setStatusModalOpen(true);
    },
    [getMarkupStatusDisplay],
  );

  const closeStatusModal = useCallback(() => {
    if (statusUpdateBusy) {
      return;
    }
    setStatusModalOpen(false);
    setPendingStatusMarkup(null);
    setSelectedStatusValue("open");
  }, [statusUpdateBusy]);

  const submitStatusUpdate = useCallback(async () => {
    const markupId = Number(pendingStatusMarkup?.id);
    if (!Number.isInteger(markupId) || markupId <= 0) {
      return;
    }

    const statusPatch = buildMarkupStatusPayload(
      pendingStatusMarkup,
      selectedStatusValue,
    );
    const updatedMarkup = {
      id: markupId,
      ...statusPatch,
    };

    setStatusUpdateBusy(true);

    try {
      await postChunkedCompressedMarkupUpdate(
        updatedMarkup,
        "pershubUpdateStatus",
      );

      const updatedMarkups = (phReviewsMarkups || []).map((item) =>
        Number(item?.id) === markupId
          ? {
              ...item,
              ...updatedMarkup,
            }
          : item,
      );

      dispatch(
        updateGlobalState({
          value: updatedMarkups,
          stateName: PH_REVIEWS_MARKUPS,
        }),
      );
      setFilteredMarkups((prev) =>
        (prev || []).map((item) =>
          Number(item?.id) === markupId
            ? {
                ...item,
                ...updatedMarkup,
              }
            : item,
        ),
      );
      setSelectedMarkup((prev) =>
        Number(prev?.id) === markupId
          ? {
              ...prev,
              ...updatedMarkup,
            }
          : prev,
      );

      closeStatusModal();
      fetchUserMarkups();
    } catch (error) {
      console.log(error);
    } finally {
      setStatusUpdateBusy(false);
    }
  }, [
    buildMarkupStatusPayload,
    closeStatusModal,
    dispatch,
    fetchUserMarkups,
    pendingStatusMarkup,
    phReviewsMarkups,
    postChunkedCompressedMarkupUpdate,
    selectedStatusValue,
  ]);
  const createdByOptions = createdByList
    ? [
        ...createdByList.map((option) => {
          return { value: option, label: option };
        }),
      ]
    : [{ value: "", label: "" }];

  const projectOptions = useMemo(() => {
    if (!phReviewsMarkups?.length) {
      return [{ value: "", label: "" }];
    }

    const uniqueProjectIds = removeDuplicates(
      phReviewsMarkups
        .map((markup) => markup?.projectId)
        .filter((projectId) => projectId !== null && projectId !== undefined)
        .map((projectId) => projectId.toString()),
    );

    return uniqueProjectIds
      .map((projectId) => {
        const matchedProject = projectList?.find(
          (project) =>
            project?.id?.includes(projectId) || projectId.includes(project?.id),
        );

        return {
          value: projectId,
          label: matchedProject?.text || matchedProject?.name || projectId,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [phReviewsMarkups, projectList]);

  const filterMarkups = (e) => {
    let value;
    if (e.target) {
      setSearchCriteria(e.target.value);
      value = e.target.value;
    } else if (e === "true") {
      setSearchCriteria("Yes");
      value = e;
    } else if (e === "false") {
      setSearchCriteria("No");
      value = e;
    } else {
      setSearchCriteria(e);
      value = e;
    }
    const tempMarkups = phReviewsMarkups.filter((markup) => {
      switch (markupFilterCat) {
        case "project":
          return (
            markup.projectId
              ?.toString()
              .toLowerCase()
              .includes(value.toString().toLowerCase()) ||
            value
              .toString()
              .toLowerCase()
              .includes(markup.projectId?.toString().toLowerCase())
          );
        case "User":
          return markup.userName.toLowerCase().includes(value.toLowerCase());
        case "createdFor":
          return markup.createdFor
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase());
        case "approved":
          return markup.approved
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase());
        case "actioned":
          return markup.actioned
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase());
        default:
          return markup;
      }
    });
    // const tempMarkupData = {
    //   data: tempMarkups,
    // };
    setFilteredMarkups(sortMarkupsNewestFirst(tempMarkups));
  };

  const fetchUserSubmittals = async () => {
    await axios({
      method: "get",
      url: HOST_URL + "/projects/getUserSubmittals",
      params: { email: userData?.emailId },
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      withCredentials: true,
    })
      .then((response) => {
        dispatch(
          updateGlobalState({
            value: response.data,
            stateName: ALL_USER_SUBMITTALS,
          }),
        );
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const onOpenMarkup = async (markup) => {
    const project = projectList?.filter((project) => {
      return project.id.includes(markup.projectId);
    })[0];

    const markupWithProjectType = {
      ...markup,
      projectType: project?.mviewProjectType || "Unknown",
    };

    setMarkupModalLoading(true);
    setSelectedMarkup(undefined);
    setSelectedMarkupImage(undefined);
    setSelectedMarkupImageNotice("");
    setMarkupModal(true);

    try {
      const markupId = Number(markup?.id);
      const detailsResponse =
        Number.isInteger(markupId) && markupId > 0
          ? await getMarkupById(markupId)
          : null;
      const detailsPayload = detailsResponse
        ? normalizeMarkupByIdPayload(detailsResponse)
        : null;

      const resolvedMarkup = {
        ...markupWithProjectType,
        ...(detailsPayload || {}),
      };

      setSelectedMarkup(resolvedMarkup);
      await fetchMainMarkupScreenshot(resolvedMarkup);
    } finally {
      setMarkupModalLoading(false);
    }
  };

  const onCloseMarkupModal = () => {
    setShowMarkupModelsModal(false);
    setMarkupModal(false);
    setMarkupModalLoading(false);
    setSelectedMarkup(undefined);
    setSelectedMarkupImage(undefined);
    setSelectedMarkupImageNotice("");
  };

  const onCloseModal = () => {
    setSubmissionData();
    setSelectedMarkupImage();
    setSelectedMarkupImageNotice("");
    setHasUnsavedModelManagementComment(false);
    setUnsavedCommentPromptOpen(false);
    setPendingModelManagementAction(null);
    setSubmitModalOpen(false);
  };

  const hasModelManagementCommentDraft = () => {
    if (hasUnsavedModelManagementComment) {
      return true;
    }

    const commentInput = document.querySelector(
      ".pershub-model-management-modal .comments input.form-control",
    );

    return Boolean(String(commentInput?.value || "").trim());
  };

  const runModelManagementAction = (action) => {
    if (action === "check") {
      updateReview("check");
      return;
    }

    updateReview("review");
  };

  const closeUnsavedCommentPrompt = () => {
    setUnsavedCommentPromptOpen(false);
    setPendingModelManagementAction(null);
  };

  const confirmUnsavedCommentProceed = () => {
    const action = pendingModelManagementAction;
    closeUnsavedCommentPrompt();

    if (!action) {
      return;
    }

    runModelManagementAction(action);
  };

  const confirmModelManagementAction = () => {
    const action = selectedTab === "check" ? "check" : "review";

    if (hasModelManagementCommentDraft()) {
      setPendingModelManagementAction(action);
      setUnsavedCommentPromptOpen(true);
      return;
    }

    runModelManagementAction(action);
  };

  const openApproveConfirmModal = () => {
    if (!selectedReview) {
      return;
    }

    setApproveConfirmModalOpen(true);
  };

  const closeApproveConfirmModal = () => {
    setApproveConfirmModalOpen(false);
  };

  const confirmApproveReview = () => {
    setApproveConfirmModalOpen(false);
    updateReview("Approve");
  };

  const closeReviewStatusModal = () => {
    setReviewStatusModal({
      open: false,
      title: "",
      message: "",
    });
  };

  const closeSubmittalStatusModal = () => {
    setSubmittalStatusModal({
      open: false,
      title: "",
      message: "",
    });
  };

  const closeMarkupModelsModal = () => {
    setShowMarkupModelsModal(false);
    setModalModels(null);
  };

  const onCloseSubmittalModal = () => {
    setSubmittalModal(false);
    //fetch user submittals to update redux
    fetchUserSubmittals();
    setSelectedSubmittal(null);
    setFile();
    setUploading(false);
  };
  const onCloseInfoModal = () => {
    setInfoModal(false);
    fetchUserSubmittals();
    // setSelectedSubmittal(null);
    // setReviewSubmittal(false);
    // fetchUserReviews();
    // setSelectedReview(null);
  };

  const submitModel = async () => {
    setUploading(true);
    const formData = new FormData();
    formData.append("submittalData", JSON.stringify(selectedSubmittal));
    formData.append("file", file);
    await updateSubmittalWithFile(formData);
    onCloseSubmittalModal();
  };

  const fileSelectHandler = (event) => {
    let pickedFiles;
    Array.from(event.target.files).forEach((file) => {
      if (!file.name.endsWith(selectedSubmittal?.fileType)) {
        window.alert(
          `Please select a file with the correct extension: ${selectedSubmittal?.fileType}`,
        );
        pickedFiles = "invalid";
        return;
      }
    });
    if (pickedFiles === "invalid") {
      setFile(null);
      return;
    }
    if (event.target.files) {
      pickedFiles = event.target.files[0];
      setFile(pickedFiles);
    }
  };

  const clearActiveMarkupPreview = useCallback(() => {
    setActiveMarkupCardId(null);
    setSelectedMarkup(null);
    setSelectedMarkupImage(null);
    setSelectedMarkupImageNotice("");

    if (typeof onMarkupPreviewLoadingChange === "function") {
      onMarkupPreviewLoadingChange(false);
    }

    if (typeof onMarkupPreviewSelect === "function") {
      onMarkupPreviewSelect(null);
    }
  }, [onMarkupPreviewLoadingChange, onMarkupPreviewSelect]);

  const tabSelectHandler = (selected) => {
    setSelectedReview(null);
    setSelectedSubmittal(null);
    setReviewSubmittal(false);
    setMarkupView(false);
    clearActiveMarkupPreview();
    setSelectedTab(selected);
    setSubmissionData();
  };

  const updateSubmittal = async (submittal, status) => {
    if (status === "Rejected") {
      setRejecting(true);
      if (
        window.confirm("Are you sure you would like to reject this submittal?")
      ) {
        await axios({
          method: "post",
          url: HOST_URL + "/projects/updateSubmittalStatus",
          withCredentials: true,
          data: { submittal, status, modelId: selectedModels[0].deleteId },
        });
        setReviewSubmittal(false);
        setSelectedSubmittal(null);
        setRejecting(false);

        setSubmittalStatusModal({
          open: true,
          title: "Submittal Rejected",
          message: "Submittal rejected successfully.",
        });
      }
      setRejecting(false);
    }
    if (status === "Approved") {
      setApproving(true);
      await axios({
        method: "post",
        url: HOST_URL + "/projects/updateSubmittalStatus",
        withCredentials: true,
        data: { submittal, status, modelId: selectedModels[0].deleteId },
      });
      setReviewSubmittal(false);
      setSelectedSubmittal(null);
      setApproving(false);
      setSubmittalStatusModal({
        open: true,
        title: "Submittal Approved",
        message: "Submittal approved successfully.",
      });
    }
    fetchUserSubmittals();
    //axios call tp update status of the submittal based on status, if rejected then dleete file from ACC and remove from DB, if approved then mark as approved in db.
    // await axios({
    //   method: "post",
    //   url: HOST_URL + "/projects/updateSubmittalStatus",
    //   withCredentials: true,
    //   data: { submittal, status, modelId: selectedModels[0].deleteId },
    // });
    // setReviewSubmittal(false);
    // setSelectedSubmittal(null);
    // setRejecting(false);
    // setApproving(false);
  };

  /////////// AG-GRID STARTING //////////
  const gridRef = useRef(null);

  const columnDefs = [
    {
      headerName: "Document Number",
      width: 250,
      valueGetter: (p) => {
        return p.data.projectAssetTaskVersion.projectAssetTask.docNumber;
      },
    },
    {
      headerName: "Title",
      width: 200,
      valueGetter: (p) => {
        return p.data.projectAssetTaskVersion.projectAssetTask.task;
      },
    },
    {
      headerName: "Status",
      width: 150,
      valueGetter: (p) => {
        return p.data.projectAssetTaskVersion.mviewStatus;
      },
    },
    // {
    //   field: "revision",
    //   width: 150,
    // },
    {
      headerName: "Revision",
      width: 150,
      valueGetter: (p) => {
        return p.data.projectAssetTaskVersion.mviewRevision;
      },
    },
  ];

  // DefaultColDef sets props common to all Columns
  const defaultColDef = useMemo(() => ({
    sortable: true,

    filter: "agSetColumnFilter",
  }), []);

  const updateSubmittalComments = async (submittal, comments) => {
    //updateSelectedSubmittal with new comments
    setSelectedSubmittal({ ...submittal, comments });
    //update submittal in backend with these new comments
    await axios({
      method: "post",
      url: HOST_URL + "/projects/updateSubmittalComments",
      withCredentials: true,
      data: { submittalId: submittal.id, comments },
    });
  };

  const updateReviewComments = async (review, comments) => {
    //updateSelectedReview with new comments
    setSelectedReview({ ...review, comments });
    await axios({
      method: "post",
      url: HOST_URL + "/phReviews/updateReviewComments",
      withCredentials: true,
      data: { reviewId: review.id, comments },
    });
  };

  const updateMarkupComments = async (markup, comments) => {
    const newMarkup = { ...markup, comments };
    const patchMarkup = {
      id: markup?.id,
      comments,
    };
    setSelectedMarkup(newMarkup);
    updateMarkup(patchMarkup);
  };

  const reviewButton = (o, key) => {
    return (
      <li
        key={o.id + key}
        className={`list-group-item d-flex justify-content-between align-items-center ph-list-item ${
          selectedReview?.id === o?.id ? "ph-list-item--selected" : ""
        }`}
        id={o.id}
        onClick={(e) => {
          loadModelHandler(o.id);
        }}
      >
        {o?.projectAssetTask?.docNumber +
          " - " +
          o?.projectAssetTask?.task +
          " - " +
          o?.proposedStatus +
          " requested"}
      </li>
    );
  };

  const exitEditMode = () => {
    setCancelRejectConfirmModalOpen(true);
  };

  const closeCancelRejectConfirmModal = () => {
    setCancelRejectConfirmModalOpen(false);
  };

  const confirmCancelRejection = () => {
    setSubmissionData();
    setMarkupView(false);
    setCancelRejectConfirmModalOpen(false);
  };

  const rejectReviewWithMarkup = async () => {
    setIssueRejectConfirmModalOpen(true);
  };

  const closeIssueRejectConfirmModal = () => {
    setIssueRejectConfirmModalOpen(false);
  };

  const confirmIssueRejection = async () => {
    if (issuingRejectionWithMarkup) {
      return;
    }

    setIssuingRejectionWithMarkup(true);

    try {
      const selectedDocNumber = selectedReview?.projectAssetTask?.docNumber;
      const titlePrefix = selectedDocNumber
        ? `${selectedDocNumber} - Rejection Markup`
        : "Rejection Markup";
      const timestamp = new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
      const autoSaveTitle = `${titlePrefix} (${timestamp})`;

      const createdForEmails = [
        selectedReview?.submittedBy,
        selectedReview?.reviewer,
        selectedReview?.approver,
        selectedReview?.checkedBy,
        selectedReview?.reviewedBy,
        selectedReview?.approvedBy,
        userData?.emailId,
      ]
        .map((value) => String(value || "").trim())
        .filter((value) => value.includes("@"));

      const uniqueCreatedForEmails = Array.from(new Set(createdForEmails));

      const autoSaveHandler = rejectionMarkupAutoSaveRef.current;
      if (typeof autoSaveHandler !== "function") {
        setReviewStatusModal({
          open: true,
          title: "Unable to Reject Review",
          message:
            "Markup save handler is unavailable. Please close and reopen rejection mode, then try again.",
        });
        return;
      }

      const saveResult = await autoSaveHandler({
        title: autoSaveTitle,
        createdForEmails: uniqueCreatedForEmails,
      });

      if (!saveResult?.success || !saveResult?.markupId) {
        setReviewStatusModal({
          open: true,
          title: "Unable to Reject Review",
          message:
            saveResult?.error ||
            "Could not save the rejection markup. Please place a pin and try again.",
        });
        return;
      }

      updateReview("Reject", saveResult.markupId);
      setMarkupView(false);
      setIssueRejectConfirmModalOpen(false);
    } finally {
      setIssuingRejectionWithMarkup(false);
    }
  };

  const getUrnsFromModels = () => {
    if (!modalModels || !Array.isArray(modalModels)) return [];

    try {
      return modalModels
        .map((version) => {
          const modelId = version.projectAssetTaskVersion?.modelId;
          return modelId;
        })
        .filter(Boolean);
    } catch (error) {
      console.error("Error extracting URNs:", error);
      setViewerError(error);
      return [];
    }
  };

  const handleViewerReady = (viewer, modelArray) => {};

  const handleViewerError = (error) => {
    console.error("Viewer error:", error);
    setViewerError(error);
  };

  const getCreatedForList = (createdFor) => {
    if (!createdFor) return [];
    if (Array.isArray(createdFor)) {
      return createdFor.filter(Boolean);
    }
    if (typeof createdFor === "string") {
      return createdFor
        .split(",")
        .map((user) => user.trim())
        .filter(Boolean);
    }

    return [];
  };

  const fetchAndLoadMarkup = async (markupId) => {
    const numericMarkupId = Number(markupId);

    if (!Number.isInteger(numericMarkupId) || numericMarkupId <= 0) {
      const fallbackMarkup = {
        ...(selectedReview || {}),
        id: null,
        title:
          selectedReview?.projectAssetTask?.docNumber ||
          "Rejected Review",
        rejectionReview: selectedReview || null,
      };

      setMarkupModalLoading(false);
      setSelectedMarkup(fallbackMarkup);
      setSelectedMarkupImage(null);
      setSelectedMarkupImageNotice(
        "No markup was saved with this rejection.",
      );
      setMarkupModal(true);
      return;
    }

    const data = await getMarkupById(numericMarkupId);
    const loadedMarkup = data?.data || { id: numericMarkupId };
    onOpenMarkup({
      ...loadedMarkup,
      rejectionReview: selectedReview || null,
    });
  };

  const getRejectionDetails = () => {
    const source =
      selectedMarkup?.rejectionReview ||
      (selectedReview &&
      (selectedReview?.approvedDate === "rejected" ||
        selectedReview?.reviewedDate === "rejected")
        ? selectedReview
        : null);

    if (!source) {
      return null;
    }

    const rejectedBy =
      source?.rejectedBy ||
      source?.checkedBy ||
      source?.reviewedBy ||
      source?.approvedBy ||
      "Not recorded";

    const rejectedDateRaw =
      source?.rejectedDate ||
      source?.checkedDate ||
      source?.reviewedDate ||
      source?.approvedDate ||
      "";

    const rejectedDate =
      !rejectedDateRaw ||
      rejectedDateRaw === "rejected" ||
      rejectedDateRaw === "archived"
        ? "Not recorded"
        : rejectedDateRaw;

    return {
      rejectedBy,
      rejectedDate,
      stage:
        selectedTab === "approval"
          ? "Approval"
          : selectedTab === "review"
            ? "Review"
            : selectedTab === "check"
              ? "Check"
              : "Review",
    };
  };

  const rejectionDetails = getRejectionDetails();

  return (
    <div className={`pers-hub${markupView ? " pers-hub--rejection-mode" : ""}`}>
      <div className="row align-items-center pers-hub__header">
        <div className="col">
          <h4 className="pers-hub__title">Personal Hub</h4>
        </div>

        <div className="col-auto pers-hub__refresh">
          <a
            className="nav-link pers-hub__refresh-link"
            href={PERSONALHUB_PAGE}
            title="Refresh Personal Hub"
            // target="_blank"
            // rel="noopener noreferrer"
          >
            <button
              type="button"
              className="btn btn-sm btn-outline-primary pers-hub__refresh-btn"
            >
              <img
                src={refresh}
                alt="Refresh"
                className="pers-hub__refresh-icon"
              />
            </button>
          </a>
        </div>
      </div>
      <div className="container pers-hub__body">
        <div className="bs-component pers-hub__surface">
          <ul className="nav nav-tabs pers-hub__tabs">
            <li className="nav-item">
              <a
                className="nav-link active ph-tab"
                data-bs-toggle="tab"
                href="#submitted"
                onClick={(e) => {
                  tabSelectHandler("submitted");
                }}
              >
                Submissions
              </a>
            </li>
            <li className="nav-item">
              <a
                className="nav-link ph-tab"
                data-bs-toggle="tab"
                href="#check"
                onClick={(e) => {
                  tabSelectHandler("check");
                }}
              >
                {persHubNotifications?.numberOfChecks > 0 ? (
                  <>
                    <span>Checks</span>
                    <span className="tab__badge">
                      {persHubNotifications?.numberOfChecks}
                    </span>
                  </>
                ) : (
                  <span>Checks</span>
                )}
              </a>
            </li>
            <li className="nav-item">
              <a
                className="nav-link ph-tab"
                data-bs-toggle="tab"
                href="#review"
                onClick={(e) => {
                  tabSelectHandler("review");
                }}
              >
                {persHubNotifications?.numberOfReviews > 0 ? (
                  <>
                    <span>Reviews</span>
                    <span className="tab__badge">
                      {persHubNotifications?.numberOfReviews}
                    </span>
                  </>
                ) : (
                  <span>Reviews</span>
                )}
              </a>
            </li>
            <li className="nav-item">
              <a
                className="nav-link ph-tab"
                data-bs-toggle="tab"
                href="#approval"
                onClick={(e) => {
                  tabSelectHandler("approval");
                }}
              >
                {persHubNotifications?.numberOfApprovals > 0 ? (
                  <>
                    <span>Approvals</span>
                    <span className="tab__badge">
                      {persHubNotifications?.numberOfApprovals}
                    </span>
                  </>
                ) : (
                  <span>Approvals</span>
                )}
              </a>
            </li>
            {/* <li className="nav-item ph-tab">
              <a className="nav-link" data-bs-toggle="tab" href="#archived">
              Archived
              </a>
            </li> */}
            <li className="nav-item">
              <a
                className="nav-link ph-tab"
                data-bs-toggle="tab"
                href="#markups"
                onClick={(e) => {
                  tabSelectHandler("markup");
                }}
              >
                {persHubNotifications?.numberOfMarkups > 0 ? (
                  <>
                    <span>Markups</span>
                    <span className="tab__badge">
                      {persHubNotifications?.numberOfMarkups}
                    </span>
                  </>
                ) : (
                  <span>Markups</span>
                )}
              </a>
            </li>
            <li className="nav-item">
              <a
                className="nav-link ph-tab"
                data-bs-toggle="tab"
                href="#submittals"
                onClick={(e) => {
                  tabSelectHandler("submittals");
                }}
              >
                {persHubNotifications?.numberOfSupSubmittals +
                  persHubNotifications?.numberOfResSubmittals >
                0 ? (
                  <>
                    <span>Submittals</span>
                    <span className="tab__badge">
                      {persHubNotifications?.numberOfSupSubmittals +
                        persHubNotifications?.numberOfResSubmittals}
                    </span>
                  </>
                ) : (
                  <span>Submittals</span>
                )}
              </a>
            </li>
          </ul>
          <div
            id="myTabContent"
            ref={tabContentRef}
            className="tab-content overflow-auto pers-hub__tab-content"
          >
            <div className="tab-pane fade show active" id="submitted">
              <p className="mb-1">These are documents you have submitted</p>
              <h5>Checks:</h5>
              <ul className="list-group ">
                {phReviewsInfo.submitted?.map((review, i) => {
                  if (review?.checkedDate === null) {
                    return reviewButton(review, i);
                    // return (
                    //   <li
                    //     key={i}
                    //     className="list-group-item d-flex justify-content-between align-items-center ph-list-item"
                    //     id="massBalance"
                    //     onClick={(e) => {
                    //       loadModelHandler(review.id);
                    //     }}
                    //     style={{
                    //       color:
                    //         selectedReview?.id === review?.id && "#31c224ff",
                    //     }}
                    //   >
                    //     {review?.projectAssetTask?.docNumber +
                    //       " - " +
                    //       review?.projectAssetTask?.task +
                    //       " - " +
                    //       review?.proposedStatus +
                    //       " requested"}
                    //   </li>
                    // );
                  } else {
                    return <div key={"BLANK" + i}></div>;
                  }
                })}
              </ul>
              <h5>Reviews:</h5>
              <ul className="list-group ">
                {phReviewsInfo.submitted?.map((review, i) => {
                  if (
                    review.reviewedDate === null &&
                    review.checkedDate !== null
                  ) {
                    return reviewButton(review, i);
                    // return (
                    //   <li
                    //     key={i}
                    //     className="list-group-item d-flex justify-content-between align-items-center ph-list-item"
                    //     id="massBalance"
                    //     onClick={(e) => {
                    //       loadModelHandler(review.id);
                    //     }}
                    //     style={{
                    //       color:
                    //         selectedReview?.id === review?.id && "#31c224ff",
                    //     }}
                    //   >
                    //     {review?.projectAssetTask?.docNumber +
                    //       " - " +
                    //       review?.projectAssetTask?.task +
                    //       " - " +
                    //       review?.proposedStatus +
                    //       " requested"}
                    //   </li>
                    // );
                  } else {
                    return <div key={"BLANK" + i}></div>;
                  }
                })}
              </ul>
              <h5>Approvals:</h5>
              <ul className="list-group  mt-2">
                {phReviewsInfo.submitted?.map((review, i) => {
                  if (
                    review.approvedDate === null &&
                    review.reviewedDate !== null
                  ) {
                    return reviewButton(review, i);

                    // return (
                    //   <li
                    //     key={i}
                    //     className="list-group-item d-flex justify-content-between align-items-center ph-list-item"
                    //     id="massBalance"
                    //     onClick={(e) => loadModelHandler(review.id)}
                    //     style={{
                    //       color:
                    //         selectedReview?.id === review?.id && "#31c224ff",
                    //     }}
                    //   >
                    //     {review?.projectAssetTask?.docNumber +
                    //       " - " +
                    //       review?.projectAssetTask?.task +
                    //       " - " +
                    //       review?.proposedStatus +
                    //       " requested"}
                    //   </li>
                    // );
                  } else {
                    return <div key={"BLANK" + i}></div>;
                  }
                })}
              </ul>
              <h5>Rejections:</h5>
              <ul className="list-group  mt-2">
                {phReviewsInfo.archived?.map((review, i) => {
                  if (
                    review.approvedDate === "rejected" ||
                    review.reviewedDate === "rejected"
                  ) {
                    return reviewButton(review, i);
                    // return (
                    //   <li
                    //     key={i}
                    //     className="list-group-item d-flex justify-content-between align-items-center ph-list-item"
                    //     id={review.id}
                    //     onClick={(e) => loadModelHandler(review.id)}
                    //     style={{
                    //       color:
                    //         selectedReview?.id === review?.id && "#31c224ff",
                    //     }}
                    //   >
                    //     {review?.projectAssetTask?.docNumber +
                    //       " - " +
                    //       review?.projectAssetTask?.task +
                    //       " - " +
                    //       review?.proposedStatus +
                    //       " requested"}
                    //   </li>
                    // );
                  } else {
                    return <div key={"BLANK" + i}></div>;
                  }
                })}
              </ul>
            </div>
            <div className="tab-pane fade" id="check">
              <p className="mb-1">
                These are documents that require you to check
              </p>
              <ul className="list-group ">
                {phReviewsInfo.forCheck?.map((review, i) => {
                  return reviewButton(review, i);
                })}
              </ul>
            </div>
            <div className="tab-pane fade" id="review">
              <p className="mb-1">
                These are documents that require your review
              </p>
              <ul className="list-group ">
                {phReviewsInfo.forReview?.map((review, i) => {
                  return reviewButton(review, i);
                })}
              </ul>
            </div>
            <div className="tab-pane fade" id="approval">
              <p className="mb-1">
                These are documents that require your approval
              </p>
              <ul className="list-group ">
                {phReviewsInfo.forApproval?.map((review, i) => {
                  return reviewButton(review, i);
                })}
              </ul>
            </div>
            <div className="tab-pane fade" id="submittals">
              <p className="mb-1">These submittals require your attention.</p>
              <ul className="list-group ">
                {allUserSubmittals?.allSupplierRepSubmittals?.length > 0 &&
                  allUserSubmittals?.allSupplierRepSubmittals?.map(
                    (submittal, i) => {
                      return (
                        <li
                          key={i}
                          className="list-group-item d-flex justify-content-between align-items-center ph-list-item"
                          id={i + "-submittal"}
                          onClick={(e) => {
                            setSelectedSubmittal(submittal);
                            setReviewSubmittal(false);
                            setSubmittalModal(true);
                          }} //need to use main page for information instead of model.
                        >
                          {submittal.projectAssetTask.projectAsset.project
                            .name +
                            " - " +
                            submittal.projectAssetTask.docNumber}
                        </li>
                      );
                    },
                  )}
              </ul>
              <ul className="list-group ">
                {allUserSubmittals?.allResponsiblePersonSubmittals?.length >
                  0 &&
                  allUserSubmittals?.allResponsiblePersonSubmittals?.map(
                    (submittal, i) => {
                      return (
                        <li
                          key={i}
                          className={`list-group-item d-flex justify-content-between align-items-center ph-list-item ${
                            selectedSubmittal?.id === submittal?.id
                              ? "ph-list-item--selected"
                              : ""
                          }`}
                          id={i + "-submittal"}
                          onClick={(e) => {
                            setSelectedSubmittal(submittal);
                            setReviewSubmittal(true);
                            loadSubmittalModel(submittal);
                            // loadModelHandler(review.id);//need to get the model id(urn) and also the document name to pass into selectedModels
                          }}
                        >
                          {submittal.projectAssetTask.projectAsset.project
                            .name +
                            " - " +
                            submittal.projectAssetTask.docNumber}
                        </li>
                      );
                    },
                  )}
              </ul>
            </div>
            {/* <div className="tab-pane fade" id="archived">
              <p className="mb-1">
                These are your submitted documents that have been archived
              </p>
              <ul className="list-group calc_list_items">
                {phReviewsInfo.archived?.map((review) => {
                  return (
                    <li
                      className="list-group-item d-flex justify-content-between align-items-center"
                      id="massBalance"
                      onClick={(e) => loadModelHandler(review.id)}>
                      {review.projectName + " - " + review.documentName}
                    </li>
                  );
                })}
              </ul>
            </div> */}
            <div className="tab-pane fade pers-hub__markup-pane" id="markups">
              <p className="mb-1">
                These are markups that require your attention
              </p>
              <Select
                className="acc-project-select"
                classNamePrefix="acc-project-select"
                onChange={markupFilterHandler}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={customStyles}
                options={selectOptions}
                defaultValue={selectOptions[0]}
              />
              {markupFilterCat ? (
                markupFilterCat === "actioned" ||
                markupFilterCat === "approved" ? (
                  <>
                    <br></br>

                    <div>
                      <Select
                        className="acc-project-select"
                        classNamePrefix="acc-project-select"
                        onChange={(e) => filterMarkups(e.value)}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={customStyles}
                        options={actionOptions}
                        value={{ label: searchCriteria, value: searchCriteria }}
                        // defaultValue={actionOptions[0]}
                      />
                    </div>
                  </>
                ) : markupFilterCat === "User" ? (
                  <>
                    <br></br>
                    <div>
                      <Select
                        className="acc-project-select"
                        classNamePrefix="acc-project-select"
                        onChange={(e) => filterMarkups(e.value)}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={customStyles}
                        options={createdByOptions}
                        value={{ label: searchCriteria, value: searchCriteria }}
                        // defaultValue={actionOptions[0]}
                      />
                    </div>
                  </>
                ) : markupFilterCat === "project" ? (
                  <>
                    <br></br>
                    <div>
                      <Select
                        className="acc-project-select"
                        classNamePrefix="acc-project-select"
                        onChange={(e) => filterMarkups(e.value)}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={customStyles}
                        options={projectOptions}
                        value={
                          projectOptions.find(
                            (option) => option.value === searchCriteria,
                          ) || null
                        }
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <br></br>
                    <div className="form-floating mb-3 markup-filter">
                      <input
                        type="input"
                        className="form-control markup-filterText"
                        id="floatingInput"
                        placeholder="username"
                        value={searchCriteria}
                        onChange={(e) => filterMarkups(e)}
                      />
                      <label
                        htmlFor="floatingInput"
                        className="markup-filterText"
                      >
                        Enter search criteria
                      </label>
                    </div>
                  </>
                )
              ) : (
                <></>
              )}
              <ul ref={markupPreviewListRef} className="markup-list pers-hub__markup-list">
                {filteredMarkups?.map((markup, i) => {
                  const project = projectList?.filter((project) => {
                    return project.id.includes(markup.projectId);
                  })[0];
                  const statusDisplay = getMarkupStatusDisplay(markup);
                  return (
                    <div
                      className="card text-white bg-secondary mb-3"
                      style={{ maxWidth: "100%" }}
                      key={markup.id}
                    >
                      <div className="card-header markup-header d-flex justify-content-between align-items-center">
                        <p
                          className="card-text markup-text mb-0"
                          style={{
                            color:
                              String(activeMarkupCardId) === String(markup.id)
                                ? "#93d500"
                                : undefined,
                            fontWeight:
                              String(activeMarkupCardId) === String(markup.id)
                                ? 700
                                : undefined,
                          }}
                        >
                          {markup.title}
                        </p>
                        {project?.mviewProjectType !== "Legacy" && (
                          <span
                            className="badge"
                            style={{
                              ...(statusDisplay.badgeStyle || {}),
                              borderRadius: "999px",
                              fontSize: "11px",
                            }}
                          >
                            {statusDisplay.label}
                          </span>
                        )}
                      </div>

                      <div
                        className="card-body markup-body"
                        onClick={() => onMarkupCardClick(markup)}
                      >
                        <div className="row">
                          <div
                            className="col-4"
                            data-markup-id={markup.id}
                            style={{
                              paddingRight: "0",
                              paddingLeft: "17px",
                              paddingTop: "5px",
                              paddingBottom: "5px",
                            }}
                          >
                            <img
                              className="markup-preview"
                              src={
                                markupImages[markup.id]?.imagePreview || undefined
                              }
                              decoding="async"
                              alt=""
                            ></img>
                          </div>
                          <div
                            className="col"
                            style={{
                              paddingLeft: "2px",
                              paddingTop: "5px",
                            }}
                          >
                            <p className="card-text markup-text">
                              Process Area: {markup.processArea}
                            </p>
                            <p className="card-text markup-text">
                              Created by: {markup.userName}
                            </p>
                            <p className="card-text markup-text">
                              Created on: {markup.createdDate}
                            </p>
                          </div>
                          {project?.mviewProjectType !== "Legacy" && (
                            <div
                              className="col-auto d-flex flex-column align-items-start gap-1"
                              style={{
                                paddingTop: "5px",
                                paddingRight: "0px",
                                paddingLeft: "6px",
                                marginRight: "20px",
                              }}
                            >
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary screenshot-markup__load-btn screenshot-markup__info-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onOpenMarkup(markup);
                                }}
                              >
                                Info
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary screenshot-markup__load-btn screenshot-markup__info-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openStatusModal(markup);
                                }}
                              >
                                Status
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
        {reviewSubmittal && (
          <div className="pers-hub__action-panel">
            <div className="mt-2 row">
              <div className="col">
                <h5 style={{ margin: 0 }}>Submittal Actions:</h5>
              </div>
            </div>
            <div className="row mt-2">
              <div className=" col">
                <button
                  className="btn mwh-btn-primary center btn-sm"
                  onClick={(e) => {
                    setInfoModal(true);
                  }}
                >
                  {approving ? (
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : (
                    <span> More Info</span>
                  )}
                </button>
              </div>
              <div className=" col">
                <button
                  className="btn btn-success center btn-sm"
                  onClick={() => {
                    launchModelViewer();
                  }}
                >
                  <span> View Model</span>
                </button>
              </div>
              <div className=" col ">
                <button
                  className="btn btn-success center btn-sm"
                  onClick={(e) => {
                    updateSubmittal(selectedSubmittal, "Approved");
                  }}
                >
                  {approving ? (
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : (
                    <span> Approve</span>
                  )}
                </button>
              </div>
              <div className=" col ">
                <button
                  className="btn btn-danger center btn-sm"
                  onClick={(e) =>
                    updateSubmittal(selectedSubmittal, "Rejected")
                  }
                >
                  {rejecting ? (
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : (
                    <span> Reject</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        {selectedReview && (
          <div className="pers-hub__action-panel">
            <div className="mt-2 row">
              <div className="col">
                <h5 style={{ margin: 0 }}>Review Actions:</h5>
              </div>
            </div>
            <div className="row mt-2">
              <div className=" col">
                <button
                  className="btn mwh-btn-primary center btn-sm"
                  onClick={(e) => {
                    if (selectedReview.approvedDate === "rejected") {
                      fetchAndLoadMarkup(selectedReview.markupId);
                    } else {
                      setInfoModal(true);
                    }
                  }}
                >
                  <span> More Info</span>
                </button>
              </div>
              <div className=" col">
                <button
                  className="btn btn-success center btn-sm"
                  onClick={() => {
                    launchModelViewer();
                  }}
                >
                  <span> View Model</span>
                </button>
              </div>

              {(selectedTab === "check" || selectedTab === "review") && (
                <>
                  <div className="col">
                    <button
                      className="btn btn-success center btn-sm"
                      onClick={(e) => {
                        setSubmitModalOpen(true);
                        selectedTab === "review" &&
                          setSubmissionData({
                            ...submissionData,
                            approver: selectedReview.approvedBy,
                          });
                        // window.alert("Review approved successfully.");
                      }}
                    >
                      Approve
                    </button>
                  </div>
                  <div className=" col ">
                    <button
                      className="btn btn-danger center btn-sm"
                      // onClick={(e) => updateReview("Reject")}
                      onClick={(e) => {
                        launchModelViewer();
                        setMarkupView(true);
                      }}
                    >
                      Reject
                    </button>
                  </div>
                </>
              )}

              {selectedTab === "submitted" &&
                (selectedReview?.reviewedDate === "rejected" ||
                selectedReview?.approvedDate === "rejected" ? (
                  <div className="col">
                    <button
                      className="btn btn-danger center btn-sm"
                      onClick={(e) => updateReview("Archive")}
                    >
                      Archive
                    </button>
                  </div>
                ) : (
                  <div className="col">
                    <button
                      className="btn btn-danger center btn-sm"
                      onClick={(e) => deleteReview(selectedReview.id)}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              {selectedTab === "approval" && (
                <>
                  <div className="col">
                    <button
                      className="btn btn-success center btn-sm"
                      onClick={openApproveConfirmModal}
                    >
                      Approve
                    </button>
                  </div>
                  <div className="col">
                    <button
                      className="btn btn-danger center btn-sm"
                      onClick={(e) => {
                        launchModelViewer();
                        setMarkupView(true);
                      }}
                    >
                      Reject
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        {markupView && (
          <>
            <div className="pers-hub__markup-editor">
              <div className="row mt-2 pers-hub__rejection-comment-row">
                {/* <h4 style={{ marginTop: "8px", marginBottom: "0px" }}>
                  Current Comments:
                </h4>
                <div style={{ height: "200px" }}>
                  <Comments
                    currentUser={userData?.emailId}
                    comments={selectedReview?.comments}
                    updateReview={(e) => {
                      updateReviewComments(selectedReview, e);
                    }}
                    // updateSubmittal={(e) => {
                    //   updateSubmittalComments(selectedSubmittal, e);
                    // }}
                  />
                </div> */}
                <Form.Label className="mt-1">Add a comment</Form.Label>
                <Form.Control
                  className="pers-hub__rejection-comment-input"
                  rows={4}
                  as="textarea"
                  onChange={(e) => {
                    setSubmissionData({
                      ...submissionData,
                      comments: {
                        id: 1,
                        user: userData?.emailId,
                        date: new Date().toISOString(),
                        comment: e.target.value,
                      },
                    });
                  }}
                  value={submissionData?.comments?.comment}
                />
              </div>
              <div className="row mt-2">
                <div className=" col">
                  <ScreenshotMarkup
                    viewerState={viewerState}
                    isPanelCollapsed
                    disableManualSave
                    onRegisterRejectionAutoSave={registerRejectionMarkupAutoSave}
                  />
                  <div className="d-flex gap-2 mt-2">
                    <button
                      type="button"
                      className="btn btn-danger markupButton3"
                      onClick={() => exitEditMode()}
                    >
                      Cancel Rejection
                    </button>
                    <button
                      type="button"
                      className="btn btn-success markupButton3"
                      onClick={() => rejectReviewWithMarkup()}
                    >
                      Issue Rejection
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      {/* MODALS */}
      <div>
        <Modal
          show={markupModal}
          onHide={onCloseMarkupModal}
          dialogClassName="markup-modal project-info-modal markup-detail-modal"
          centered
        >
          <Modal.Header className="project-info-modal__header markup-detail-modal__header">
            {showMarkupModelsModal ? (
              <ButtonGroup className="ms-4 me-4 markup-detail-modal__header-actions">
                <Button
                  id="archive"
                  type="button"
                  variant="outline-danger"
                  className="project-status-modal__action-btn markup-detail-modal__action-btn"
                  // checked={selectedMarkup?.actioned}
                  // value={selectedMarkup?.id}
                  // onChange={(e) => toggleButtonChangeHandler(e)}
                  onClick={(e) => closeMarkupModelsModal()}
                >
                  GO BACK
                </Button>
              </ButtonGroup>
            ) : (
              <></>
            )}
            <Modal.Title className="markup-detail-modal__title">
              {selectedMarkup?.title}
            </Modal.Title>
            <div
              data-bs-theme="dark"
              style={{
                position: "absolute",
                right: 20,
              }}
            >
              <button
                type="button"
                className="btn-close project-info-modal__close"
                onClick={onCloseMarkupModal}
                aria-label="Close"
              />
            </div>
          </Modal.Header>
          {markupModalLoading ? (
            <Modal.Body className="project-info-modal__body markup-detail-modal__body">
              <div className="markup-detail-modal__loading">
                Loading markup details...
              </div>
            </Modal.Body>
          ) : showMarkupModelsModal ? (
            <>
              <Modal.Body className="project-info-modal__body markup-detail-modal__body">
                <div className="row">
                  <div className="col d-flex justify-content-center markup-detail-modal__image-col">
                    {selectedMarkupImage ? (
                      <img
                        className="markup-detail-modal__image"
                        style={{ maxWidth: "100%", maxHeight: "100%" }}
                        src={selectedMarkupImage}
                        alt="markup"
                      ></img>
                    ) : (
                      <div className="markup-detail-modal__loading">
                        {selectedMarkupImageNotice || "No markup image available."}
                      </div>
                    )}
                  </div>
                  <div className="col markup-detail-modal__viewer-col">
                    <div
                      className="markup-detail-modal__viewer-wrap"
                      style={{
                        height: "70vh",
                        width: "100%",
                        position: "relative",
                      }}
                    >
                      {viewerError ? (
                        <div className="d-flex justify-content-center align-items-center h-100">
                          <div className="text-center">
                            <p className="text-danger">
                              Error loading viewer: {viewerError.message}
                            </p>
                            <button
                              className="btn btn-outline-success project-status-modal__action-btn markup-detail-modal__action-btn"
                              onClick={() => {
                                setViewerError(null);
                                setModalReady(false);
                                setTimeout(() => setModalReady(true), 500);
                              }}
                            >
                              Retry
                            </button>
                          </div>
                        </div>
                      ) : modalReady &&
                        modalModels &&
                        modalModels.length > 0 ? (
                        <ReusableViewer
                          urns={getUrnsFromModels()}
                          // urns={testUrns.map(o => o.urn)}
                          onViewerReady={handleViewerReady}
                          onViewerError={handleViewerError}
                          viewerId={`markupModelsViewer`} // Unique ID each time
                          height="100%"
                          width="100%"
                          markupState={selectedMarkup?.markupState}
                        />
                      ) : (
                        <div className="d-flex justify-content-center align-items-center h-100">
                          <p>
                            {!modalReady
                              ? "Preparing viewer..."
                              : "No models available to display"}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Modal.Body>
            </>
          ) : (
            <>
              <Modal.Body className="project-info-modal__body markup-detail-modal__body">
                <div className="row">
                  <div className="col-4">
                    <div
                      className="row markup-detail-modal__meta"
                      style={{ marginBottom: "8px" }}
                    >
                      <div className="col-6" style={{ paddingRight: "4px" }}>
                        <h4 style={{ marginTop: "8px", marginBottom: "0px" }}>
                          Process Area:
                        </h4>
                        <p>{selectedMarkup?.processArea}</p>

                        <h4 style={{ marginTop: "8px", marginBottom: "0px" }}>
                          Created By:
                        </h4>
                        <p>{selectedMarkup?.userName || "None"}</p>

                        <h4 style={{ marginTop: "8px", marginBottom: "0px" }}>
                          Created Date:
                        </h4>
                        <p>{selectedMarkup?.createdDate}</p>

                        {rejectionDetails && (
                          <>
                            <h4 style={{ marginTop: "8px", marginBottom: "0px" }}>
                              Rejected By:
                            </h4>
                            <p>{rejectionDetails.rejectedBy}</p>

                            <h4 style={{ marginTop: "8px", marginBottom: "0px" }}>
                              Rejected Date:
                            </h4>
                            <p>{rejectionDetails.rejectedDate}</p>

                            <h4 style={{ marginTop: "8px", marginBottom: "0px" }}>
                              Rejected At Stage:
                            </h4>
                            <p>{rejectionDetails.stage}</p>
                          </>
                        )}
                      </div>
                      <div
                        className="col-6 d-flex flex-column"
                        style={{ paddingLeft: "4px" }}
                      >
                        <h4 style={{ marginTop: "8px", marginBottom: "0px" }}>
                          Assigned to:
                        </h4>
                        <div
                          style={{
                            height: "150px",
                            overflowY: "auto",
                          }}
                        >
                          {(() => {
                            const assignedUsers = getCreatedForList(
                              selectedMarkup?.createdFor,
                            );

                            if (assignedUsers.length === 0) {
                              return <p className="mb-0">None</p>;
                            }

                            return (
                              <ul
                                style={{ marginBottom: 0, paddingLeft: "18px" }}
                              >
                                {assignedUsers.map((user, idx) => (
                                  <li key={`${user}-${idx}`}>{user}</li>
                                ))}
                              </ul>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                    <h4 style={{ marginTop: "8px", marginBottom: "0px" }}>
                      Comments:
                    </h4>
                    <div
                      className="markup-detail-modal__comments-wrap"
                      style={{ height: "200px" }}
                    >
                      <Comments
                        currentUser={userData?.emailId}
                        comments={selectedMarkup?.comments}
                        updateMarkup={(e) => {
                          updateMarkupComments(selectedMarkup, e);
                        }}
                      />
                    </div>

                    {selectedMarkup?.selectedModels && (
                      <>
                        <h4 style={{ marginTop: "8px", marginBottom: "0px" }}>
                          Selected Models:
                        </h4>
                        <div
                          className="markup-detail-modal__grid-wrap"
                          style={{
                            marginTop: "5px",
                            height: "200px",
                            width: "100%",
                          }}
                        >
                          <AgGridReact
                            theme={agGridTheme}
                            ref={gridRef}
                            rowData={selectedMarkup?.versions}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            gridOptions={agGridOptionsNone}
                          />
                        </div>
                      </>
                    )}
                    <div
                      style={{
                        marginTop: "10px",
                        display: "block",
                      }}
                    >
                      <div className="text-center">
                        <p style={{ fontSize: 12, marginBottom: "2px" }}>
                          The table above shows the status and revision of the
                          selected models when the markup was made.
                        </p>
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline-danger project-status-modal__action-btn markup-detail-modal__action-btn"
                        onClick={onCloseMarkupModal}
                        style={{ marginLeft: "4px" }}
                      >
                        Close
                      </button>
                      {/* {selectedMarkup?.projectType !== "Legacy" && (
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={(e) => openMarkupModelsModal()}
                        >
                          View
                        </button>
                      )} */}
                    </div>
                  </div>
                  <div className="col-8 d-flex justify-content-center markup-detail-modal__image-col">
                    {selectedMarkupImage ? (
                      <img
                        className="markup-detail-modal__image"
                        style={{ maxWidth: "100%", maxHeight: "100%" }}
                        src={selectedMarkupImage}
                        alt="markup"
                      ></img>
                    ) : (
                      <div className="markup-detail-modal__loading">
                        {selectedMarkupImageNotice || "No markup image available."}
                      </div>
                    )}
                  </div>
                </div>
              </Modal.Body>
            </>
          )}
        </Modal>
      </div>
      <Modal
        show={approveConfirmModalOpen}
        onHide={closeApproveConfirmModal}
        dialogClassName="project-info-modal app-confirm-modal"
        centered
      >
        <Modal.Header className="project-info-modal__header">
          <Modal.Title>Approve Review</Modal.Title>
          <div
            data-bs-theme="dark"
            style={{
              position: "absolute",
              right: 20,
            }}
          >
            <button
              type="button"
              className="btn-close project-info-modal__close"
              onClick={closeApproveConfirmModal}
              aria-label="Close"
            />
          </div>
        </Modal.Header>
        <Modal.Body className="project-info-modal__body app-confirm-modal__body">
          <p className="app-confirm-modal__copy">
            Are you sure you want to approve this review?
          </p>
          <div className="app-confirm-modal__actions">
            <button
              type="button"
              className="btn btn-outline-success project-status-modal__action-btn"
              onClick={confirmApproveReview}
            >
              Yes, Approve
            </button>
            <button
              type="button"
              className="btn btn-outline-danger project-status-modal__action-btn"
              onClick={closeApproveConfirmModal}
            >
              Cancel
            </button>
          </div>
        </Modal.Body>
      </Modal>
      <Modal
        show={cancelRejectConfirmModalOpen}
        onHide={closeCancelRejectConfirmModal}
        dialogClassName="project-info-modal app-confirm-modal"
        centered
      >
        <Modal.Header className="project-info-modal__header">
          <Modal.Title>Cancel Rejection</Modal.Title>
          <div
            data-bs-theme="dark"
            style={{
              position: "absolute",
              right: 20,
            }}
          >
            <button
              type="button"
              className="btn-close project-info-modal__close"
              onClick={closeCancelRejectConfirmModal}
              aria-label="Close"
            />
          </div>
        </Modal.Header>
        <Modal.Body className="project-info-modal__body app-confirm-modal__body">
          <p className="app-confirm-modal__copy">
            Are you sure you want to cancel the rejection? All markup progress will be lost.
          </p>
          <div className="app-confirm-modal__actions">
            <button
              type="button"
              className="btn btn-outline-danger project-status-modal__action-btn"
              onClick={confirmCancelRejection}
            >
              Yes, Cancel Rejection
            </button>
            <button
              type="button"
              className="btn btn-outline-success project-status-modal__action-btn"
              onClick={closeCancelRejectConfirmModal}
            >
              Go Back
            </button>
          </div>
        </Modal.Body>
      </Modal>
      <Modal
        show={issueRejectConfirmModalOpen}
        onHide={closeIssueRejectConfirmModal}
        dialogClassName="project-info-modal app-confirm-modal"
        centered
      >
        <Modal.Header className="project-info-modal__header">
          <Modal.Title>Issue Rejection</Modal.Title>
          <div
            data-bs-theme="dark"
            style={{
              position: "absolute",
              right: 20,
            }}
          >
            <button
              type="button"
              className="btn-close project-info-modal__close"
              onClick={closeIssueRejectConfirmModal}
              aria-label="Close"
            />
          </div>
        </Modal.Header>
        <Modal.Body className="project-info-modal__body app-confirm-modal__body">
          <p className="app-confirm-modal__copy">
            Are you sure you want to reject this review? The current screenshot markup will be saved and attached to this rejection.
          </p>
          <div className="app-confirm-modal__actions">
            <button
              type="button"
              className="btn btn-outline-success project-status-modal__action-btn"
              onClick={confirmIssueRejection}
              disabled={issuingRejectionWithMarkup}
            >
              {issuingRejectionWithMarkup
                ? "Saving Markup..."
                : "Yes, Save and Issue Rejection"}
            </button>
            <button
              type="button"
              className="btn btn-outline-danger project-status-modal__action-btn"
              onClick={closeIssueRejectConfirmModal}
              disabled={issuingRejectionWithMarkup}
            >
              Go Back
            </button>
          </div>
        </Modal.Body>
      </Modal>
      <Modal
        show={reviewStatusModal.open}
        onHide={closeReviewStatusModal}
        dialogClassName="project-info-modal app-confirm-modal"
        centered
      >
        <Modal.Header className="project-info-modal__header">
          <Modal.Title>{reviewStatusModal.title}</Modal.Title>
          <div
            data-bs-theme="dark"
            style={{
              position: "absolute",
              right: 20,
            }}
          >
            <button
              type="button"
              className="btn-close project-info-modal__close"
              onClick={closeReviewStatusModal}
              aria-label="Close"
            />
          </div>
        </Modal.Header>
        <Modal.Body className="project-info-modal__body app-confirm-modal__body">
          <p className="app-confirm-modal__copy">{reviewStatusModal.message}</p>
          <div className="app-confirm-modal__actions">
            <button
              type="button"
              className="btn btn-outline-success project-status-modal__action-btn"
              onClick={closeReviewStatusModal}
            >
              OK
            </button>
          </div>
        </Modal.Body>
      </Modal>
      <Modal
        show={submittalStatusModal.open}
        onHide={closeSubmittalStatusModal}
        dialogClassName="project-info-modal app-confirm-modal"
        centered
      >
        <Modal.Header className="project-info-modal__header">
          <Modal.Title>{submittalStatusModal.title}</Modal.Title>
          <div
            data-bs-theme="dark"
            style={{
              position: "absolute",
              right: 20,
            }}
          >
            <button
              type="button"
              className="btn-close project-info-modal__close"
              onClick={closeSubmittalStatusModal}
              aria-label="Close"
            />
          </div>
        </Modal.Header>
        <Modal.Body className="project-info-modal__body app-confirm-modal__body">
          <p className="app-confirm-modal__copy">
            {submittalStatusModal.message}
          </p>
          <div className="app-confirm-modal__actions">
            <button
              type="button"
              className="btn btn-outline-success project-status-modal__action-btn"
              onClick={closeSubmittalStatusModal}
            >
              OK
            </button>
          </div>
        </Modal.Body>
      </Modal>
      <Modal
        show={statusModalOpen}
        onHide={closeStatusModal}
        dialogClassName="markup-modal project-info-modal"
        centered
      >
        <Modal.Header className="project-info-modal__header">
          <Modal.Title>Update Markup Status</Modal.Title>
          <div
            data-bs-theme="dark"
            style={{
              position: "absolute",
              right: 20,
            }}
          >
            <button
              type="button"
              className="btn-close project-info-modal__close"
              onClick={closeStatusModal}
              aria-label="Close"
              disabled={statusUpdateBusy}
            />
          </div>
        </Modal.Header>
        <Modal.Body className="project-info-modal__body">
          <Form.Label className="mt-1">Markup</Form.Label>
          <Form.Control
            readOnly
            value={pendingStatusMarkup?.title || ""}
            className="mb-3"
          />
          <Form.Label className="mt-1">Status</Form.Label>
          <Select
            className="save-markup-modal__select acc-project-select"
            classNamePrefix="acc-project-select"
            menuPortalTarget={document.body}
            styles={customStyles}
            options={markupStatusOptions}
            value={
              markupStatusOptions.find(
                (option) => option.value === selectedStatusValue,
              ) || markupStatusOptions[0]
            }
            onChange={(option) => {
              setSelectedStatusValue(option?.value || "open");
            }}
            isDisabled={statusUpdateBusy}
          />
          <div className="d-flex gap-2 mt-3">
            <Button
              variant="outline-success"
              className="project-status-modal__action-btn"
              onClick={submitStatusUpdate}
              disabled={statusUpdateBusy || !pendingStatusMarkup}
            >
              {statusUpdateBusy ? "Updating..." : "Update Status"}
            </Button>
            <Button
              variant="outline-danger"
              className="project-status-modal__action-btn"
              onClick={closeStatusModal}
              disabled={statusUpdateBusy}
            >
              Cancel
            </Button>
          </div>
        </Modal.Body>
      </Modal>
      <Modal
        show={submitModalOpen}
        onHide={onCloseModal}
        dialogClassName="markup-modal project-info-modal save-markup-modal pershub-modal pershub-model-management-modal"
        centered
      >
        <Modal.Header className="project-info-modal__header save-markup-modal__header">
          <Modal.Title>Model Management Form</Modal.Title>
          <div
            data-bs-theme="dark"
            style={{
              position: "absolute",
              right: 20,
            }}
          >
            <button
              type="button"
              className="btn-close project-info-modal__close"
              onClick={onCloseModal}
              aria-label="Close"
            />
          </div>
        </Modal.Header>
        <Modal.Body className="project-info-modal__body save-markup-modal__body">
          <div className="row g-3">
            <div className="col">
              {selectedTab === "check" && (
                <>
                  <Form.Label className="save-markup-modal__label">
                    Who is the reviewer?
                    <MandatoryFieldIndicator />
                  </Form.Label>

                  <Select
                    className="save-markup-modal__select acc-project-select"
                    classNamePrefix="acc-project-select"
                    menuPortalTarget={document.body}
                    styles={customStyles}
                    onChange={(e) => {
                      setSubmissionData({
                        ...submissionData,
                        reviewer: e.value,
                      });
                    }}
                    options={allProjectUsers
                      .filter((o) => reviewerRoles.includes(o.role))
                      .map((user) => {
                        return { label: user.email, value: user.email };
                      })}
                    value={
                      submissionData?.reviewer
                        ? {
                            value: submissionData?.reviewer,
                            label: submissionData?.reviewer,
                          }
                        : null
                    }
                    placeholder="Select reviewer"
                  />
                </>
              )}
              <Form.Label className="mt-1 save-markup-modal__label">
                Who is the approver?
                <MandatoryFieldIndicator />
              </Form.Label>
              <Select
                className="save-markup-modal__select acc-project-select"
                classNamePrefix="acc-project-select"
                menuPortalTarget={document.body}
                styles={customStyles}
                onChange={(e) => {
                  setSubmissionData({
                    ...submissionData,
                    approver: e.value,
                  });
                }}
                options={allProjectUsers
                  .filter((o) => approverRoles.includes(o.role))
                  .map((user) => {
                    return { label: user.email, value: user.email };
                  })}
                value={
                  submissionData?.approver
                    ? {
                        value: submissionData?.approver,
                        label: submissionData?.approver,
                      }
                    : null
                }
                placeholder="Select approver"
              />
            </div>
            <div className="col ">
              <Form.Label className="mt-1 save-markup-modal__label">Comments</Form.Label>
              {/* <Form.Control
                rows={4}
                as="textarea"
                onChange={(e) => {
                  setSubmissionData({
                    ...submissionData,
                    comments: e.target.value,
                  });
                }}
                value={submissionData?.comments}
              /> */}
              <div style={{ height: "200px" }}>
                <Comments
                  currentUser={userData?.emailId}
                  comments={selectedReview?.comments}
                  onUnsavedDraftChange={setHasUnsavedModelManagementComment}
                  updateReview={(e) => {
                    updateReviewComments(selectedReview, e);
                  }}
                  // updateSubmittal={(e) => {
                  //   updateSubmittalComments(selectedSubmittal, e);
                  // }}
                />
              </div>
            </div>
          </div>
          <div className="save-markup-modal__actions">
            <Button
              type="button"
              variant="outline-success"
              className="project-status-modal__action-btn save-markup-modal__action-btn"
              onClick={confirmModelManagementAction}
              disabled={
                selectedTab === "check"
                  ? !submissionData?.approver || !submissionData?.reviewer
                  : !submissionData?.approver
              }
            >
              {selectedTab === "check"
                ? "Confirm ok for review"
                : "Confirm ok for approval"}
            </Button>
            <Button
              type="button"
              variant="outline-danger"
              className="project-status-modal__action-btn save-markup-modal__action-btn"
              onClick={onCloseModal}
            >
              Cancel
            </Button>
          </div>
          <MandatoryExplanation />
        </Modal.Body>
      </Modal>
      <Modal
        show={unsavedCommentPromptOpen}
        onHide={closeUnsavedCommentPrompt}
        dialogClassName="project-info-modal app-confirm-modal"
        centered
      >
        <Modal.Header className="project-info-modal__header">
          <Modal.Title>Unsaved Comment</Modal.Title>
          <div
            data-bs-theme="dark"
            style={{
              position: "absolute",
              right: 20,
            }}
          >
            <button
              type="button"
              className="btn-close project-info-modal__close"
              onClick={closeUnsavedCommentPrompt}
              aria-label="Close"
            />
          </div>
        </Modal.Header>
        <Modal.Body className="project-info-modal__body app-confirm-modal__body">
          <p className="app-confirm-modal__copy">
            You have written a comment that has not been submitted. Continue
            without submitting it?
          </p>
          <div className="app-confirm-modal__actions">
            <button
              type="button"
              className="btn btn-outline-success project-status-modal__action-btn"
              onClick={confirmUnsavedCommentProceed}
            >
              Continue
            </button>
            <button
              type="button"
              className="btn btn-outline-danger project-status-modal__action-btn"
              onClick={closeUnsavedCommentPrompt}
            >
              Go Back
            </button>
          </div>
        </Modal.Body>
      </Modal>
      <Modal
        show={submittalModal}
        onHide={onCloseSubmittalModal}
        dialogClassName="markup-modal project-info-modal pershub-submittal-modal"
        center="true"
      >
        <Modal.Header className="project-info-modal__header pershub-submittal-modal__header">
          <Modal.Title>Submittal Details</Modal.Title>
          <div
            data-bs-theme="dark"
            style={{
              position: "absolute",
              right: 20,
            }}
          >
            <button
              type="button"
              className="btn-close project-info-modal__close"
              onClick={onCloseSubmittalModal}
              aria-label="Close"
            />
          </div>
        </Modal.Header>
        <Modal.Body className="project-info-modal__body pershub-submittal-modal__body">
          <div className="row pershub-submittal-modal__grid">
            <div className="col pershub-submittal-modal__section">
              <Form.Label className="mt-1">Project</Form.Label>
              <Form.Control
                readOnly
                value={
                  selectedSubmittal?.projectAssetTask?.projectAsset?.project
                    ?.name
                }
              />
              <Form.Label className="mt-1">Drawing number</Form.Label>
              <Form.Control
                readOnly
                value={selectedSubmittal?.projectAssetTask?.docNumber}
              />
              <Form.Label className="mt-1">Drawing description</Form.Label>
              <Form.Control
                readOnly
                value={selectedSubmittal?.projectAssetTask?.task}
              />
            </div>
            <div className="col pershub-submittal-modal__section">
              <Form.Label className="form-label mt-1">Comments</Form.Label>
              <div className="pershub-submittal-modal__comments">
                <Comments
                  currentUser={userData?.emailId}
                  comments={selectedSubmittal?.comments}
                  updateSubmittal={(e) => {
                    updateSubmittalComments(selectedSubmittal, e);
                  }}
                />
              </div>
              <Form.Label className="form-label mt-1" htmlFor="modelUpload">
                File upload
              </Form.Label>
              <input
                className="form-control cad-upload-file-input pershub-submittal-modal__file-input"
                type="file"
                id="modelUpload"
                accept={selectedSubmittal?.fileType}
                onChange={(e) => fileSelectHandler(e)}
              />
              <ButtonGroup className="pershub-submittal-modal__actions">
                <Button
                  className="mt-2 project-status-modal__action-btn"
                  variant="outline-success"
                  size="sm"
                  onClick={(e) => submitModel()}
                  disabled={!file}
                >
                  {uploading ? (
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : (
                    <span>Submit file</span>
                  )}
                </Button>
              </ButtonGroup>
            </div>
          </div>
        </Modal.Body>
      </Modal>
      <Modal
        show={infoModal}
        onHide={onCloseInfoModal}
        dialogClassName="markup-modal pershub-modal pershub-info-modal"
        center="true"
      >
        <Modal.Header className="pershub-info-modal__header">
          <Modal.Title>Review Details:</Modal.Title>
          <div
            data-bs-theme="dark"
            style={{
              position: "absolute",
              right: 20,
            }}
          >
            <button
              type="button"
              className="btn-close"
              onClick={onCloseInfoModal}
              aria-label="Close"
            />
          </div>
        </Modal.Header>
        {selectedSubmittal && reviewSubmittal && (
          <Modal.Body className="pershub-info-modal__body">
            <div className="row pershub-info-modal__grid">
              <div className="col pershub-info-modal__section">
                <Form.Label className="mt-1">Project</Form.Label>
                <Form.Control
                  readOnly
                  value={
                    selectedSubmittal?.projectAssetTask?.projectAsset?.project
                      ?.name
                  }
                />
                <Form.Label className="mt-1">Drawing number</Form.Label>
                <Form.Control
                  readOnly
                  value={selectedSubmittal?.projectAssetTask?.docNumber}
                />
                <Form.Label className="mt-1">Drawing description</Form.Label>
                <Form.Control
                  readOnly
                  value={selectedSubmittal?.projectAssetTask?.task}
                />
              </div>
              <div className="col pershub-info-modal__section">
                <Form.Label className="form-label mt-1">Comments</Form.Label>
                <div className="pershub-info-modal__comments">
                  <Comments
                    currentUser={userData?.emailId}
                    comments={selectedSubmittal?.comments}
                    updateSubmittal={(e) => {
                      updateSubmittalComments(selectedSubmittal, e);
                    }}
                  />
                </div>
              </div>
            </div>
          </Modal.Body>
        )}
        {selectedReview && (
          <Modal.Body className="pershub-info-modal__body">
            <div className="row pershub-info-modal__grid">
              <div className="col pershub-info-modal__section">
                <Form.Label className="mt-1">Project</Form.Label>
                <Form.Control
                  className="form-control form-control-sm"
                  readOnly
                  value={
                    selectedReview?.projectAssetTask?.projectAsset?.project.name
                  }
                />
                <Form.Label className="mt-1">Regional Designation</Form.Label>
                <Form.Control
                  className="form-control form-control-sm"
                  readOnly
                  value={
                    selectedReview?.projectAssetTask?.projectAsset?.asset?.name
                  }
                />
                <Form.Label className="mt-1">MWH Asset</Form.Label>
                <Form.Control
                  className="form-control form-control-sm"
                  readOnly
                  value={selectedReview?.projectAssetTask?.mwhAssetName}
                />
                <Form.Label className="mt-1">Model</Form.Label>
                <Form.Control
                  className="form-control form-control-sm"
                  readOnly
                  value={selectedReview?.projectAssetTask?.docNumber}
                />
                <Form.Label className="mt-1">Target status</Form.Label>
                <Form.Control
                  className="form-control form-control-sm"
                  readOnly
                  value={selectedReview?.proposedStatus}
                />
                <Form.Label className="mt-1">Submitted By</Form.Label>
                <Form.Control
                  className="form-control form-control-sm"
                  readOnly
                  value={selectedReview?.submittedBy}
                />
                {selectedReview?.checkedBy && selectedReview?.checkedDate && (
                  <>
                    <Form.Label className="mt-1">Checked By</Form.Label>
                    <Form.Control
                      className="form-control form-control-sm"
                      readOnly
                      value={selectedReview?.checkedBy}
                    />
                  </>
                )}
                {selectedReview?.reviewedBy && selectedReview?.reviewedDate && (
                  <>
                    <Form.Label className="mt-1">Reviewed By</Form.Label>
                    <Form.Control
                      className="form-control form-control-sm"
                      readOnly
                      value={selectedReview?.reviewedBy}
                    />
                  </>
                )}
              </div>
              <div className="col pershub-info-modal__section">
                <Form.Label className="form-label mt-1">Comments</Form.Label>
                <div className="pershub-info-modal__comments">
                  <Comments
                    currentUser={userData?.emailId}
                    comments={selectedReview?.comments}
                    updateReview={(e) => {
                      updateReviewComments(selectedReview, e);
                    }}
                    // updateSubmittal={(e) => {
                    //   updateSubmittalComments(selectedSubmittal, e);
                    // }}
                  />
                </div>
              </div>
            </div>
          </Modal.Body>
        )}
      </Modal>
    </div>
  );
};

export default PersHub;
